/**
 * Gewerk-übergreifender Bauteil-Katalog (Single Source of Truth).
 *
 * In Sprint A.1 enthält der Katalog die 25 historischen Wand-/Fassaden-
 * Einträge (9 AW + 8 IW + 8 FAS) aus dem vormaligen `wall-catalog.ts`,
 * jetzt typisiert über `CatalogEntry` mit `gewerk`-Feld. Über die Sprints
 * B–F wächst der Katalog auf ~290 Codes über 25 Gewerk-Prefixes
 * (BO/WA/DE/SAN/HZ/EL/...) zur Single Source of Truth für die fünf
 * Deliverables LV / Raumbuch / Türenliste / Fensterliste / Prüfwerkzeug.
 *
 * **Backwards-Compat**: Type-Aliase `WallType` und `FacadeType` bleiben
 * erhalten, ebenso die Default-Konstanten `DEFAULT_WALL_CATALOG` und
 * `DEFAULT_FACADE_CATALOG`. `wall-catalog.ts` ist ein Re-Export-Stub auf
 * dieses Modul.
 *
 * **Plan-Mapping & LV-Mapping**: Felder `planzeichen`, `stempelKeys`,
 * `stempelMatchPatterns`, `inlineTextPatterns`, `benoetigteFachplanung`,
 * `lvMapping` und `vorschlagFuer` sind in Sprint A.1 für alle 25
 * Bestands-Einträge bewusst leer. Sie werden in Sprints A.4–A.7 + B–F
 * kontrolliert befüllt.
 */

// ============================================================================
// GEWERK + FACHPLANER + BAUTEIL-TYP
// ============================================================================

/**
 * Zwei- bis vierstellige Prefixes für Bauteil-Codes. 25 Werte aus
 * `recherche/code_jegewerk.txt` plus 4 zusätzliche aus
 * `recherche/code_jezeichnung.txt` (FAS / UG / VB / ABD).
 */
export type GewerkPrefix =
  // 25 Top-Level-Gewerke aus code_jegewerk.txt
  | "AW" // Außenwandaufbauten
  | "IW" // Innenwandaufbauten
  | "BO" // Bodenaufbauten
  | "WA" // Wandoberflächen
  | "DE" // Deckenoberflächen
  | "SAN" // Sanitär-Objekte
  | "TW" // Trinkwasser
  | "AWL" // Abwasser
  | "EL" // Elektro
  | "DA" // Daten / SmartHome
  | "HZ" // Heizung
  | "WW" // Warmwasser
  | "LUE" // Lüftung
  | "KL" // Klima
  | "DAH" // Dach
  | "FE" // Fenster
  | "TU" // Türen
  | "GA" // Außenanlagen / GaLaBau
  | "ERD" // Erdarbeiten
  | "GR" // Gründung
  | "MB" // Metallbau
  | "BS" // Brandschutz
  | "SICH" // Sicherheit
  | "AUF" // Aufzüge
  | "SCH" // Schadstoffsanierung
  // 4 zusätzliche Prefixes aus code_jezeichnung.txt
  | "FAS" // Fassade
  | "UG" // Untergrund
  | "VB" // Vorbereitung
  | "ABD"; // Abdichtung

/**
 * Fachplaner-Rollen für Nachweis-Tracking (Prüfwerkzeug). Pro Code-Eintrag
 * gibt `benoetigteFachplanung` an, wessen Freigabe für die LV-Übernahme
 * erforderlich ist.
 */
export type Fachplaner =
  | "architekt"
  | "tragwerk"
  | "bauphysik"
  | "brandschutz"
  | "schallschutz"
  | "tga-sanitaer"
  | "tga-heizung"
  | "tga-lueftung"
  | "elektroplaner"
  | "abdichtungsplaner"
  | "fassadenplaner"
  | "dachplaner"
  | "landschaftsarchitekt"
  | "geotechniker"
  | "akustikplaner"
  | "innenarchitekt"
  | "schadstoffgutachter"
  | "aufzugsplaner"
  | "sicherheitsplaner"
  | "energieberater";

/**
 * Plan-Typen, die ein Architektur-Plan-PDF darstellen kann. Sprint A.3/A.4
 * macht die Auswahl beim Upload zur Pflicht und schreibt sie in
 * `00-plan-annotations.json`. MVP-Pipeline läuft voll bei `grundriss` und
 * `schnitt`; andere Typen werden klassifiziert + abgelegt, aber die
 * Polygon-/Mengen-Phasen werden übersprungen (Sprint H erweitert das).
 */
export type PlanTyp =
  | "grundriss"
  | "schnitt"
  | "detail"
  | "ansicht-fassade"
  | "lageplan"
  | "elektroplan"
  | "sanitaerplan"
  | "heizungsplan"
  | "lueftungsplan"
  | "brandschutzplan"
  | "statik-bewehrung";

/**
 * Plan-Typen, für die im MVP die Polygon-/Mengen-Pipeline (Floodfill +
 * polygon-builder + dimension-resolver + room-classifier + code-assigner)
 * läuft. Andere Typen werden im plan-annotations-agent klassifiziert +
 * abgelegt; Phasen 3-7 werden übersprungen.
 *
 * Sprint P (2026-05-03): `schnitt` wurde aus dieser Liste entfernt — Schnitt-
 * Pläne haben keine Räume im Floodfill-Sinne. Sie laufen jetzt als Vision-
 * Pfad (siehe `MVP_VISION_PLAN_TYPES`).
 */
export const MVP_GEOMETRIC_PLAN_TYPES: readonly PlanTyp[] = [
  "grundriss",
];

/**
 * Sprint P (2026-05-03) — Plan-Typen, für die im MVP ein spezialisierter
 * Vision-Pass läuft (statt der geometrischen Pipeline).
 * - `schnitt`: `runSchnittParser` extrahiert Etagen-Höhen, Decken-Stärken,
 *   Dachneigung etc. → `00-plan-annotations.json#schnittData`. Cross-Run-
 *   Bridge versorgt Grundriss-Polygone mit `polygon.rh` (Sprint X).
 * - `ansicht-fassade`: `runAnsichtStempelFang` liest die Verblender-Maßzahl-
 *   Box → `00-plan-annotations.json#ansichtData`. Cross-Plan-Aggregation in
 *   Sprint X summiert über alle Mappe-Ansichten ins LV (HG02.30 FAS-KLINKER).
 */
export const MVP_VISION_PLAN_TYPES: readonly PlanTyp[] = [
  "schnitt",
  "ansicht-fassade",
];

/**
 * Union beider MVP-Sets — Plan-Typen, deren Pipeline überhaupt sinnvolle
 * Outputs erzeugt. Nicht-MVP-Typen (Lageplan, Elektroplan, ...) werden nach
 * Phase 1 (classification) gestoppt.
 */
export const MVP_PLAN_TYPES: readonly PlanTyp[] = [
  ...MVP_GEOMETRIC_PLAN_TYPES,
  ...MVP_VISION_PLAN_TYPES,
];

/**
 * Bauteil-Gruppen für `vorschlagFuer.bauteilTyp` — werden vom Code-Assigner
 * (Sprint A.5) genutzt, um pro Polygon/Wand/Öffnung passende Codes
 * vorzuschlagen.
 */
export type BauteilTyp =
  | "wand-aussen"
  | "wand-innen"
  | "fassade"
  | "boden"
  | "decke"
  | "dach"
  | "fenster"
  | "tuer"
  | "sanitaer-objekt"
  | "heizung-objekt"
  | "elektro-objekt"
  | "lueftung-objekt"
  | "sonstiges";

// ============================================================================
// LV-MAPPING
// ============================================================================

/**
 * Mapping-Eintrag eines Codes auf eine LV-Position. Pro Code können mehrere
 * Mappings stehen (1:N), z.B. `AW02` (Mauerwerk + WDVS + Innenputz +
 * Außenputz) erzeugt 4 LV-Positionen.
 *
 * Das Mapping bleibt in Sprint A.1 für alle 25 Bestands-Codes leer; Sprint
 * A.7 füllt es als Vorlage für die nachfolgenden Sprints B–F.
 */
export interface LvPositionMapping {
  /** LV-Hauptgruppe (z.B. `"HG02"` für Mauerarbeiten). */
  hauptgruppe: string;
  /** LV-Untergruppe / Titel innerhalb der Hauptgruppe (z.B. `"10"`). */
  untergruppe?: string;
  /** OZ-Pattern mit `{N}`-Platzhalter für fortlaufende Position. */
  ozPattern: string;
  /** Optional: STLB-Bau-Referenz für Audit / Validation. */
  stlbReferenz?: string;
  /** Kurz-Bezeichnung (Position-Titel im LV). */
  bezeichnung: string;
  /** Langtext-Template mit Platzhaltern wie `{wandStaerke}`. */
  langtextTemplate: string;
  mengenEinheit: "m²" | "m³" | "Stück" | "m" | "kg";
  /** Formel-Ausdruck wie `"wand.flaecheM2 - oeffnungenM2"`. */
  mengenFormel: string;
  /** Optional: VOB-Abzug-Formel (Öffnungen > 2.5 m² etc.). */
  abzugFormel?: string;
  /**
   * Sprint LV-Katalog Phase 2 (2026-05-07) — Cross-Library-Identifier.
   * Pro LV-Position mehrere Bibliotheks-spezifische Codes parallel halten,
   * damit Lookup-/Mapping-Tools (Heinze-Vorschläge, sirAdos-Texte,
   * STLB-Bau-Referenzen) deterministisch matchen können.
   *
   * Wenn ein Lookup einen `pin-match` über `heinzeOz` (oder analog) findet,
   * gibt es Confidence 1.0 — keine Score-Heuristik nötig.
   *
   * Erweiterbar: künftige Bibliotheken können beliebige Keys ergänzen.
   */
  externalRefs?: {
    /** STLB-Leistungsbereich (DIN 18299 / VOB-konform), z.B. "012" Mauer, "023" Putz. */
    stlbLb?: string;
    /** STLB-Sub-Leistungsbereich, z.B. "012.04" für Öffnungen in Mauerwerk. */
    stlbSubLb?: string;
    /** Heinze-OZ (Format `XXX.YY.ZZZZ`) für Pin-Match im dreiplus-Korpus. */
    heinzeOz?: string;
    /** sirAdos-Identifier (Format laut sirAdos-Spec). */
    siradosId?: string;
    /** GAEB-DA-XML kanonische OZ (z.B. aus AVA-Software). */
    gaebOz?: string;
    /** DBD-BauKat-Identifier (Dr. Schiller). */
    dbdId?: string;
    /** Erweiterbar für künftige Bibliotheken. */
    [key: string]: string | undefined;
  };
}

// ============================================================================
// CODE-ASSIGNMENT (Sprint A.2)
// ============================================================================

/**
 * Quelle einer Code-Zuweisung. Steuert UI-Hinweis ("vom User", "aus Stempel
 * abgeleitet" etc.) und das Nachweis-Routing im Prüfwerkzeug.
 */
export type CodeAssignmentSource =
  | "user-manual"
  | "auto-stempel"
  | "auto-inline-text"
  | "auto-legend"
  | "auto-roomtype"
  | "auto-schnitt"
  | "auto-thickness"
  | "office-profile";

/**
 * Status einer Code-Zuweisung. Aggregat aus den `nachweise`-Status der
 * benötigten Fachplaner — siehe Status-Aggregation im 5-Deliverables-Plan.
 */
export type CodeAssignmentStatus =
  | "entwurf"
  | "in-pruefung"
  | "freigegeben"
  | "blockiert";

/**
 * Quellen-Typ für `planQuellenNachweis` — wo im Plan wurde der Code
 * abgeleitet. Wird zur User-Transparenz im UI angezeigt ("aus Stempel
 * 'Eltern' abgeleitet" etc.).
 */
export type PlanQuelleTyp =
  | "raumstempel"
  | "legende"
  | "office-profile"
  | "inline-text"
  | "schraffur"
  | "schnitt";

/**
 * Status eines einzelnen Fachplaner-Nachweises. Pro CodeAssignment kann jeder
 * benötigte Fachplaner einen eigenen Nachweis-Status haben.
 */
export interface NachweisStatus {
  status:
    | "fehlt"
    | "angefordert"
    | "vorliegt"
    | "geprueft"
    | "nicht-erforderlich";
  /** Wer hat den Nachweis geliefert (Fachplaner-Name oder Quelle). */
  quelle?: string;
  /** Freitext-Vermerk (Bemerkung, Bedingung, Klärungsbedarf). */
  vermerk?: string;
  /** ISO-Timestamp der letzten Statusänderung / Prüfung. */
  pruefungAm?: string;
  /**
   * Sprint Z++ (2026-05-05) — angehängte Datei (PDF, Bild). Wird vom
   * Backend-Upload-Endpoint gesetzt; pro Nachweis kann nur EINE Datei
   * hochgeladen werden (Re-Upload überschreibt).
   *
   * `pathInRun` ist der relative Pfad innerhalb von `runs/<runId>/`,
   * typisch `nachweise/<caId>-<fachplaner>.<ext>`.
   */
  fileRef?: {
    filename: string;
    sizeBytes: number;
    uploadedAt: string;
    pathInRun: string;
    /** MIME-Type, z.B. "application/pdf". */
    mimeType?: string;
  };
}

/**
 * Eine konkrete Zuweisung eines Katalog-Codes (`AW02`, `HZ-FBH-NASS`, ...) auf
 * ein Polygon (Raum-Code) oder eine Wand (Wand-/Aufbau-Code).
 *
 * Sprint A.2 führt diesen Type ein. Befüllung erfolgt über
 *   - User-Manual: aus dem Material-Editor / Code-Picker (Sprint A.8)
 *   - Auto-Stempel/-Inline-Text/-Legende: vom Code-Assigner-Agent (Sprint A.5)
 *   - Office-Profile: aus dem Plankopf-OCR-Match (Sprint F)
 *
 * **Migration der Stufe-1-Codes**: bestehende `wallTypeCode` und `facadeCode`
 * werden über {@link legacyToCodeAssignments} in CodeAssignment[] mit
 * `source="user-manual"`, `status="entwurf"` und leeren Nachweisen abgeleitet.
 */
export interface CodeAssignment {
  /** Stable ID innerhalb des Polygons / der Wand (z.B. `ca_<random>`). */
  id: string;
  /** Verweis auf {@link CatalogEntry.code}, z.B. `"AW02"`. */
  code: string;
  source: CodeAssignmentSource;
  /** ISO-Timestamp der Zuweisung. */
  zugewiesenAm: string;
  /** Pro Fachplaner ein Nachweis-Status; alle nicht aufgeführten gelten als
   *  "fehlt". */
  nachweise: Partial<Record<Fachplaner, NachweisStatus>>;
  status: CodeAssignmentStatus;
  /** Optional: wo im Plan wurde der Code abgeleitet (für Transparenz). */
  planQuellenNachweis?: {
    quelleTyp: PlanQuelleTyp;
    /** Auszug aus Stempel/Inline-Text/Legende, der gematcht hat. */
    text?: string;
    /** 0..1 — Match-Konfidenz, falls heuristisch ermittelt. */
    confidence?: number;
  };
}

/**
 * Migration-Helper: leitet aus den Legacy-Stufe-1-Feldern `wallTypeCode` und
 * `facadeCode` einer Wand eine CodeAssignment-Liste ab. Pure Function — keine
 * Seiteneffekte, kein Persist.
 *
 * Verwendung in späteren Sprints (A.5/A.7/A.8): vor jeder Bauteil-Lese-Logik
 * Helper-Aufruf, anschließend `wall.codeAssignments ?? legacy(wall)` als
 * effektive Liste benutzen. Damit bleibt das alte Datenformat lesbar, ohne
 * dass die Migration global auf einen Schlag rauschen muss.
 *
 * @param wall — Wand-Objekt mit optional `wallTypeCode` und `facadeCode`.
 * @param now — ISO-Timestamp für `zugewiesenAm` (default: jetzt).
 * @param idPrefix — Prefix für die generierte ID, default `"ca-legacy"`.
 */
export function legacyToCodeAssignments(
  wall: { wallTypeCode?: string | null; facadeCode?: string | null },
  now: string = new Date().toISOString(),
  idPrefix: string = "ca-legacy"
): CodeAssignment[] {
  const result: CodeAssignment[] = [];
  if (wall.wallTypeCode) {
    result.push({
      id: `${idPrefix}-wt-${wall.wallTypeCode}`,
      code: wall.wallTypeCode,
      source: "user-manual",
      zugewiesenAm: now,
      nachweise: {},
      status: "entwurf",
    });
  }
  if (wall.facadeCode) {
    result.push({
      id: `${idPrefix}-fc-${wall.facadeCode}`,
      code: wall.facadeCode,
      source: "user-manual",
      zugewiesenAm: now,
      nachweise: {},
      status: "entwurf",
    });
  }
  return result;
}

// ============================================================================
// VALUE-SET (Sprint A.5 + A.6)
// ============================================================================

/**
 * Werte-Set pro Polygon — Drei-Quellen-Hierarchie.
 *
 * Die effektiven Werte (`effective`) werden bevorzugt aus der höchsten
 * verfügbaren Quelle befüllt:
 *  1. **Stempel** (Sprint A.6, falls planAnnotations.perRoomStempel vorhanden)
 *  2. **Geometrie** (immer verfügbar — von polygon-builder + dimension-resolver)
 *
 * Sprint A.5 schreibt ausschließlich `effectiveSource: "geometrie"`. Sprint
 * A.6 erweitert um Stempel-Override und Drift-Berechnung.
 */
export interface PolygonValueSet {
  /** Effektive Werte für UI/Mengen-Berechnung. */
  effective: {
    /** Umfang in Lichten (m). */
    L: number;
    /** Fläche (m²). */
    A: number;
    /** Raumhöhe in Lichten — Sprint A.6 aus Stempel/Inline-Text. */
    rh?: { min: number; max?: number };
  };
  /** Welche Quelle war effective? */
  effectiveSource: "stempel" | "geometrie";
  /** Geometrie-Werte vom polygon-builder (immer da). */
  geometrie: { L: number; A: number };
  /** Stempel-Werte (Sprint A.6 — undefined wenn kein Stempel). */
  stempel?: {
    L?: number;
    A?: number;
    rhInLichten?: { min: number; max: number };
  };
  /** Drift Stempel ↔ Geometrie (Sprint A.6). */
  drift?: { lDeltaPct?: number; aDeltaPct?: number };
  /** User hat manuell zur Geometrie zurück-getoggelt (Sprint A.6). */
  userOverrideToGeometrie?: boolean;
}

// ============================================================================
// AUFBAU-SCHICHTEN (für AW/IW; später auch BO/DE)
// ============================================================================

export type WallCategory = "outer" | "inner";
export type WallTragend = "ja" | "nein" | "aussteifend" | "n/a";

/**
 * Eine Schicht in einem Wandtyp-Default. Kompatibel zur SystemWallLayer-
 * Struktur des bisherigen Material-Editors — kann direkt in den Material-
 * Stack einer Wand übernommen werden.
 */
/**
 * Schicht-Gewerk im Material-Stack einer Wand.
 *
 * Sprint A.11 (2026-05-02): `oberflaeche` ist die 4. Schicht-Kategorie für
 * Wandoberflächen-Beläge wie Tapete (dünn, hat Dicke) und Anstrich (`dickeMm: 0`).
 * Wird bei der Volumen-Berechnung in [derive-gewerke.ts](packages/core/src/pipeline/derive-gewerke.ts)
 * ignoriert (kein m³-Beitrag); im Material-Editor erscheint sie als 4. Button
 * neben Rohbau/Putz/Trockenbau. Mengen-Bewertung läuft primär über CodeAssignments
 * + lvMapping (z.B. WA-MAL-DISP, WA-TAPEZ-VLIES) — der Schicht-Eintrag dient
 * vor allem zur Visualisierung und für Schicht-Listen im Raumbuch.
 */
export type WallLayerGewerk =
  | "rohbau"
  | "putz"
  | "trockenbau"
  | "oberflaeche"
  | "sonstiges";

export interface WallTypeLayer {
  gewerk: WallLayerGewerk;
  preset: "mauerwerk" | "trockenbau" | "beton" | "custom";
  materialName: string;
  dickeMm: number;
  /** Bei Putz: 1 oder 2 — auf wie viele Seiten der Putz aufgetragen wird. */
  putzSides?: 1 | 2;
}

// ============================================================================
// CATALOG ENTRY
// ============================================================================

/**
 * Universeller Katalog-Eintrag. Pflicht-Felder: code, fullCode, gewerk,
 * shortLabel, description, normReferenz. Alle weiteren Felder sind optional
 * und nur für bestimmte Gewerke relevant (z.B. `defaultLayers` nur für
 * AW/IW/BO/DE).
 */
export interface CatalogEntry {
  // === Pflicht ===
  /** Kurz-Code für UI (z.B. "AW02", "HZ-FBH-NASS"). */
  code: string;
  /** Voll-Code als maschinenfreundlicher Identifier (z.B. "AW-MW-WDVS-PUTZ"). */
  fullCode: string;
  /** Gewerk-Prefix (29 Werte). */
  gewerk: GewerkPrefix;
  /** Kurze Bezeichnung (~40 Zeichen) für Listen-Anzeige. */
  shortLabel: string;
  /** Volle Beschreibung mit Schichten-/Eigenschaften-Zusammenfassung. */
  description: string;
  /** Norm-Referenzen für LV-Nachweise und Tooltips. */
  normReferenz: string[];

  // === Plan-Mapping (in A.1 für alle Bestands-Einträge leer) ===
  /** Plan-Symbol-Kennung (z.B. "FBH", "BSK", "T30"). */
  planzeichen?: string;
  /** Stempel-Schlüssel im Raumstempel (z.B. ["Boden:", "Wände:", "Decke:"]). */
  stempelKeys?: string[];
  /** Werte-Patterns die im Stempel auf diesen Code matchen. */
  stempelMatchPatterns?: string[];
  /** Inline-Text-Patterns (Polygon-OCR) die auf diesen Code matchen. */
  inlineTextPatterns?: string[];

  // === Fachplanung (in A.1 für alle Bestands-Einträge leer) ===
  /** Welche Fachplaner-Nachweise braucht dieser Code für LV-Freigabe? */
  benoetigteFachplanung?: Fachplaner[];

  // === LV-Mapping (in A.1 für alle Bestands-Einträge leer) ===
  /** 1:N LV-Positionen, die aus diesem Code generiert werden. */
  lvMapping?: { positions: LvPositionMapping[] };

  // === Vorschlags-Hint (in A.1 für alle Bestands-Einträge leer) ===
  /** Wofür der Code automatisch vorgeschlagen wird (Code-Assigner). */
  vorschlagFuer?: {
    raumTypen?: string[];
    bauteilTyp?: BauteilTyp;
    isDefault?: boolean;
  };

  // === Aufbau (nur AW / IW / BO / DE) ===
  /** Default-Material-Stack — vorbefüllt im Material-Editor bei Auswahl. */
  defaultLayers?: WallTypeLayer[];
  /** Typische Gesamtdicke in mm: [min, max]. */
  totalThicknessMmRange?: [number, number];
  /** Bauweise: Mauerwerk / Stahlbeton / Holzrahmen / CLT / Trockenbau / Sandwich. */
  bauweise?: string;
  /** Lastabtragend? */
  tragend?: WallTragend;

  // === Legacy-Felder (AW / IW Backwards-Compat) ===
  /** "outer" | "inner" — nur bei AW/IW. */
  category?: WallCategory;
  /** Schichten von innen nach außen — Strings für Anzeige im UI. */
  schichten?: string[];
  /** Typischer Einsatzbereich (Hilfe-Text in der Liste). */
  einsatz?: string;
  /** Nur bei Außenwänden: Standard-Fassade-Code als Vorschlag. */
  defaultFacadeCode?: string;

  // === Legacy-Felder (FAS Backwards-Compat) ===
  /** Mit welchen Wandtypen typisch kombinierbar (Voll-Codes oder Bauweise-Schlagwörter). */
  geeignetFuer?: string[];
}

// ============================================================================
// BACKWARDS-COMPAT TYPE-ALIASE
// ============================================================================

/**
 * Wandtyp (AW/IW) — Type-Alias auf `CatalogEntry` mit fixiertem `gewerk`.
 * Macht die wand-spezifischen Felder (category, schichten, defaultLayers,
 * totalThicknessMmRange, bauweise, tragend, einsatz) zu Pflichtfeldern,
 * sodass bestehende Aufrufer nicht auf Optional-Narrowing umstellen müssen.
 */
export type WallType = CatalogEntry & {
  gewerk: "AW" | "IW";
  category: WallCategory;
  schichten: string[];
  defaultLayers: WallTypeLayer[];
  totalThicknessMmRange: [number, number];
  bauweise: string;
  tragend: WallTragend;
  einsatz: string;
};

/**
 * Fassadentyp — Type-Alias auf `CatalogEntry` mit `gewerk: "FAS"`. Macht
 * `geeignetFuer` zur Pflicht.
 */
export type FacadeType = CatalogEntry & {
  gewerk: "FAS";
  geeignetFuer: string[];
};

/**
 * Override-Patches pro Run (`wall-catalog-overrides.json`). Pflichtfeld
 * `gewerk` darf weggelassen werden; wenn `category` gesetzt, leitet
 * `mergeWallCatalog` `gewerk` daraus ab (`outer→AW`, `inner→IW`). Bei
 * Fassaden-Patches wird `gewerk: "FAS"` automatisch gesetzt.
 */
export interface WallCatalogOverrides {
  walls?: Array<Partial<WallType> & { code: string }>;
  facades?: Array<Partial<FacadeType> & { code: string }>;
}

// ============================================================================
// DEFAULT CATALOG (25 Bestands-Einträge)
// ============================================================================

/** Single Source of Truth — alle Bestands-Einträge mit `gewerk`-Feld. */
export const DEFAULT_CATALOG: CatalogEntry[] = [
  // ============================================================================
  // AUSSENWÄNDE
  // ============================================================================
  {
    code: "AW01",
    fullCode: "AW-MW-MONO-PUTZ",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Mauerwerk monolithisch + Putz",
    description:
      "Außenwand monolithisch — Innenputz, hochdämmender Ziegel/Porenbeton/Leichtbeton, Außenputz",
    bauweise: "Mauerwerk",
    tragend: "ja",
    schichten: ["Innenputz 15", "Wärmedämmstein 365–490", "Außenputz 8"],
    totalThicknessMmRange: [388, 513],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Wärmedämmstein (Porenbeton T7 / HLZ)",
        dickeMm: 365,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Außenputz",
        dickeMm: 8,
        putzSides: 1,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "GEG Anlage 7"],
    einsatz: "Massivbau ohne zusätzliche Außendämmung",
    defaultFacadeCode: "FAS01",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk monolithisch Wärmedämmstein 365 mm",
          externalRefs: { heinzeOz: "012.01.0300" }, // heinze-pin: LHLz T8 AW d=36,5cm (monolith. Waermedaemmziegel)
          langtextTemplate:
            "Außenwand monolithisch in Mauerwerk aus hochdämmendem Wärmedämmstein (Porenbeton T7 oder Hochlochziegel) liefern und versetzen, Stärke {wandStaerke} mm, einschl. Mörtel und Verschnitt. Norm DIN EN 1996, GEG Anlage 7.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm auf Mauerwerk",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Außenwand-Mauerwerk, Stärke 15 mm, Gipsputz oder mineralisch, einschl. Eckschienen und Nachglättung. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "20",
          ozPattern: "04.20.{N}",
          stlbReferenz: "STLB-Bau 024 Außenputz",
          bezeichnung: "Außenputz mineralisch 8 mm auf Mauerwerk",
          externalRefs: { heinzeOz: "023.02.0030" }, // heinze-pin: Aussenwandputz PII 2lg
          langtextTemplate:
            "Außenputz mineralisch auf Außenwand-Mauerwerk, Stärke 8 mm, einschl. Putzleisten, Eckprofile und Sockelausbildung. Norm DIN EN 13914-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW02",
    fullCode: "AW-MW-WDVS-PUTZ",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Mauerwerk + WDVS",
    description:
      "Außenwand Massivbau mit WDVS — Innenputz, tragendes Mauerwerk, Kleber/Dübel, Dämmung EPS/MW/Holzfaser, Armierung, Oberputz",
    bauweise: "Mauerwerk",
    tragend: "ja",
    schichten: [
      "Innenputz 15",
      "Tragmauerwerk 175–240",
      "WDVS-Dämmung 140–200",
      "Armierung + Oberputz 10",
    ],
    totalThicknessMmRange: [340, 465],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Tragmauerwerk (KS / Porenbeton)",
        dickeMm: 240,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "WDVS-Dämmung",
        dickeMm: 180,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Armierung + Oberputz",
        dickeMm: 10,
        putzSides: 1,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "GEG Anlage 7"],
    einsatz: "sehr häufiger Neubau/Sanierung",
    defaultFacadeCode: "FAS01",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Außenwand KS/Porenbeton 240 mm",
          externalRefs: { heinzeOz: "012.02.0040" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=24cm
          langtextTemplate:
            "Tragendes Mauerwerk Außenwand aus Kalksandstein oder Porenbeton, Stärke {wandStaerke} mm, einschl. Mörtel und Verschnitt. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG02",
          untergruppe: "30",
          ozPattern: "02.30.{N}",
          stlbReferenz: "STLB-Bau 026 Wärmedämmverbundsystem",
          bezeichnung: "WDVS-Dämmung EPS/MW 180 mm geklebt + gedübelt",
          externalRefs: { heinzeOz: "023.04.0150" }, // heinze-pin: WDVS Wand EPS B1 d=180mm
          langtextTemplate:
            "Wärmedämmverbundsystem auf Außenwand, Dämmplatten EPS oder Mineralwolle Stärke 180 mm, geklebt mit Klebemörtel und mechanisch befestigt mit Dübeln. Norm DIN 55699, GEG Anlage 7.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm auf Mauerwerk",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Außenwand-Mauerwerk, Stärke 15 mm, Gipsputz oder mineralisch, einschl. Eckschienen und Nachglättung. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "20",
          ozPattern: "04.20.{N}",
          stlbReferenz: "STLB-Bau 024 Außenputz",
          bezeichnung: "Armierungsputz + Oberputz 10 mm auf WDVS",
          langtextTemplate:
            "Armierungsputz mit Glasfasergewebe und Oberputz Silikat- oder Silikonharz auf WDVS, Gesamt-Stärke 10 mm. Norm DIN 55699.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW03",
    fullCode: "AW-KS-WDVS",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Kalksandstein + WDVS",
    description:
      "Außenwand Kalksandstein mit WDVS — Innenputz, KS-Mauerwerk, WDVS, Putz",
    bauweise: "Mauerwerk (Kalksandstein)",
    tragend: "ja",
    schichten: [
      "Innenputz 15",
      "KS-Mauerwerk 175–240",
      "WDVS-Dämmung 140–200",
      "Armierung + Oberputz 10",
    ],
    totalThicknessMmRange: [340, 465],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Kalksandstein-Mauerwerk",
        dickeMm: 240,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "WDVS-Dämmung",
        dickeMm: 180,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Armierung + Oberputz",
        dickeMm: 10,
        putzSides: 1,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "DIN 4109", "GEG Anlage 7"],
    einsatz: "guter Schallschutz, Statik + Dämmung getrennt",
    defaultFacadeCode: "FAS01",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Außenwand Kalksandstein",
          externalRefs: { heinzeOz: "012.02.0040" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=24cm
          langtextTemplate:
            "Außenwand-Mauerwerk in Kalksandstein, Stärke {wandStaerke} mm, einschl. Dünnbettmörtel und Verschnitt. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm auf KS-Mauerwerk",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Kalksandstein-Außenwand, Stärke 15 mm, Gipsputz, einschl. Eckschienen. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG02",
          untergruppe: "50",
          ozPattern: "02.50.{N}",
          stlbReferenz: "STLB-Bau 047 WDVS",
          bezeichnung: "Wärmedämmverbundsystem 180 mm",
          externalRefs: { heinzeOz: "023.04.0150" }, // heinze-pin: WDVS Wand EPS B1 d=180mm
          langtextTemplate:
            "WDVS auf KS-Mauerwerk: Kleber/Dübel, Mineralwolle- oder EPS-Dämmung 180 mm, Armierungsmörtel + Gewebe, Oberputz mineralisch. Norm DIN 55699.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW04",
    fullCode: "AW-MW-2S-KLINKER",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Zweischalig mit Klinker",
    description:
      "Zweischaliges Mauerwerk mit Verblendung — Innenputz, tragende Innenschale, Kerndämmung/Luftschicht, Luftschichtanker, Klinker",
    bauweise: "Mauerwerk (zweischalig)",
    tragend: "ja",
    schichten: [
      "Innenputz 15",
      "Innenschale 175–240",
      "Kerndämmung + Luft 120–180",
      "Luftschichtanker",
      "Klinker/Verblender 115",
    ],
    totalThicknessMmRange: [425, 550],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Innenschale (KS / HLZ)",
        dickeMm: 175,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Kerndämmung + Luftschicht",
        dickeMm: 135,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Klinker / Verblender",
        dickeMm: 115,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "GEG Anlage 7"],
    einsatz: "Norddeutschland, robuste Fassade",
    defaultFacadeCode: "FAS02",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Innenschale tragend KS/HLZ",
          externalRefs: { heinzeOz: "012.02.0020" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=17,5cm (Innenschale)
          langtextTemplate:
            "Innenschale zweischaliger Außenwand in Kalksandstein/Hochlochziegel, Stärke 175–240 mm, tragend. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Innenschale, Stärke 15 mm. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG02",
          untergruppe: "60",
          ozPattern: "02.60.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Kerndämmung + Luftschicht",
          langtextTemplate:
            "Kerndämmung Mineralwolle 120–180 mm in zweischaliger Außenwand, einschl. Luftschichtanker und 4 cm Luftschicht. Norm DIN 4108.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW05",
    fullCode: "AW-MW-VHF-PLATTE",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Mauerwerk + VHF Platte",
    description:
      "Massive Außenwand mit vorgehängter hinterlüfteter Plattenfassade — Innenputz, Mauerwerk, Dämmung, UK, Hinterlüftung, Fassadenplatte",
    bauweise: "Mauerwerk",
    tragend: "ja",
    schichten: [
      "Innenputz 15",
      "Mauerwerk 175–240",
      "Dämmung 140–200",
      "UK + Hinterlüftung 40–60",
      "Fassadenplatte 8–20",
    ],
    totalThicknessMmRange: [378, 535],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Tragmauerwerk",
        dickeMm: 240,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Mineralwolle-Dämmung",
        dickeMm: 180,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Unterkonstruktion + Hinterlüftung",
        dickeMm: 50,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Fassadenplatte",
        dickeMm: 12,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "DIN 18516-1"],
    einsatz: "langlebige Fassaden mit Faserzement, HPL, Metall, Keramik",
    defaultFacadeCode: "FAS03",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Außenwand tragend 240 mm",
          externalRefs: { heinzeOz: "012.02.0040" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=24cm
          langtextTemplate:
            "Tragmauerwerk Außenwand in KS oder Porenbeton, Stärke 240 mm, einschl. Mörtel. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Außenwand-Mauerwerk, Stärke 15 mm. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG09",
          untergruppe: "20",
          ozPattern: "09.20.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Vorgehängte hinterlüftete Fassade Faserzement/HPL",
          externalRefs: { heinzeOz: "038.01.0010" }, // heinze-pin: VHF Faserzementplatte, sichtbare Befestigung
          langtextTemplate:
            "Vorgehängte hinterlüftete Plattenfassade auf Massivwand: Mineralwolle-Dämmung 180 mm, Aluminium-Unterkonstruktion, Hinterlüftung 40 mm, Faserzement-/HPL-/Keramik-Fassadenplatte 8–20 mm, einschl. Befestigung und Eckausbildung. Norm DIN 18516-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW06",
    fullCode: "AW-MW-VHF-HOLZ",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Mauerwerk + VHF Holzschalung",
    description:
      "Massive Außenwand mit hinterlüfteter Holzschalung — Innenputz, Mauerwerk, Dämmung, UK, Hinterlüftung, Holzschalung",
    bauweise: "Mauerwerk",
    tragend: "ja",
    schichten: [
      "Innenputz 15",
      "Mauerwerk 175–240",
      "Dämmung 140–200",
      "UK + Hinterlüftung 40–60",
      "Holzschalung 22–28",
    ],
    totalThicknessMmRange: [392, 543],
    defaultLayers: [
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 1,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Tragmauerwerk",
        dickeMm: 240,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Mineralwolle-Dämmung",
        dickeMm: 180,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Unterkonstruktion + Hinterlüftung",
        dickeMm: 50,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Holzschalung",
        dickeMm: 25,
      },
    ],
    normReferenz: ["DIN EN 1996", "DIN 4108", "DIN 18516-1", "DIN 68800"],
    einsatz: "Holzoptik auf Massivwand",
    defaultFacadeCode: "FAS04",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Außenwand tragend 240 mm",
          externalRefs: { heinzeOz: "012.02.0040" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=24cm
          langtextTemplate:
            "Tragmauerwerk Außenwand in KS oder Porenbeton, Stärke 240 mm. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Mauerwerk, Stärke 15 mm. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Vorgehängte hinterlüftete Holzschalung",
          langtextTemplate:
            "VHF mit Holzschalung auf Massivwand: Mineralwolle-Dämmung 180 mm, Holz-Unterkonstruktion, Hinterlüftung 40 mm, Holzschalung 22–28 mm (Lärche/Douglasie), einschl. Anstrich/Imprägnierung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW07",
    fullCode: "AW-HR-VHF-HOLZ",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Holzrahmen + VHF Holz",
    description:
      "Holzrahmen-Außenwand mit hinterlüfteter Holzfassade — Gipsplatte, Dampfbremse/OSB, Ständer + Dämmung, Holzfaserplatte, Lattung, Hinterlüftung, Holzschalung",
    bauweise: "Holzrahmenbau",
    tragend: "ja",
    schichten: [
      "Gipsplatte 12,5",
      "Dampfbremse + OSB 15",
      "Holzständer 200 + Dämmung",
      "Holzfaserplatte 40–60",
      "Lattung + Hinterlüftung 30–40",
      "Holzschalung 22–28",
    ],
    totalThicknessMmRange: [320, 380],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Gipsplatte (innen)",
        dickeMm: 12.5,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "OSB / Dampfbremse",
        dickeMm: 15,
      },
      {
        gewerk: "rohbau",
        preset: "custom",
        materialName: "Holzständer + Dämmung",
        dickeMm: 200,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Holzfaserplatte",
        dickeMm: 50,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Lattung + Hinterlüftung",
        dickeMm: 35,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Holzschalung",
        dickeMm: 25,
      },
    ],
    normReferenz: ["DIN EN 1995", "DIN 4108", "DIN 18516-1", "DIN 68800"],
    einsatz: "Holzbau, sehr verbreitet",
    defaultFacadeCode: "FAS04",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG12",
          untergruppe: "10",
          ozPattern: "12.10.{N}",
          stlbReferenz: "STLB-Bau 035 Holzbauarbeiten",
          bezeichnung: "Holzrahmenbau-Außenwand mit Dämmung",
          externalRefs: { heinzeOz: "016.06.0010" }, // heinze-pin: Aussenwand Holztafelbau, WDVS, d=295mm
          langtextTemplate:
            "Holzrahmenbauwand: Holzständer KVH 60×200 mit Mineralwolle-Dämmung 200 mm, Dampfbremse + OSB-Beplankung 15 mm raumseitig, Holzfaserplatte 50 mm außen, einschl. Tragfähigkeit und Aussteifung. Norm DIN EN 1995.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Gipsplatte raumseitig 12,5 mm + Q2",
          langtextTemplate:
            "Innenseitige Gipskartonplatte 12,5 mm auf Holzrahmen-Außenwand, Q2-Spachtelung. Norm DIN 18183-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Vorgehängte hinterlüftete Holzschalung",
          langtextTemplate:
            "VHF mit Holzschalung 22–28 mm (Lärche/Douglasie), Lattung + Hinterlüftung 30–40 mm, einschl. Anstrich/Imprägnierung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW08",
    fullCode: "AW-HR-PUTZ",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Holzrahmen + Putz",
    description:
      "Holzrahmen-Außenwand mit Putzträgerplatte und Putz — Gipsplatte/OSB, Installationsebene, Holzständer + Dämmung, Holzwerkstoffplatte, Putzträgerdämmung, Putz",
    bauweise: "Holzrahmenbau",
    tragend: "ja",
    schichten: [
      "Gipsplatte 12,5",
      "Installation + OSB 40",
      "Holzständer 200 + Dämmung",
      "Holzwerkstoffplatte 15",
      "Putzträger 80–120",
      "Putz 10",
    ],
    totalThicknessMmRange: [358, 398],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Gipsplatte (innen)",
        dickeMm: 12.5,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Installationsebene + OSB",
        dickeMm: 40,
      },
      {
        gewerk: "rohbau",
        preset: "custom",
        materialName: "Holzständer + Dämmung",
        dickeMm: 200,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Holzwerkstoffplatte",
        dickeMm: 15,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Putzträgerdämmung",
        dickeMm: 100,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Außenputz",
        dickeMm: 10,
        putzSides: 1,
      },
    ],
    normReferenz: ["DIN EN 1995", "DIN 4108", "DIN 68800"],
    einsatz: "Holzfertigbau",
    defaultFacadeCode: "FAS01",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG12",
          untergruppe: "10",
          ozPattern: "12.10.{N}",
          stlbReferenz: "STLB-Bau 035 Holzbauarbeiten",
          bezeichnung: "Holzrahmenbau-Außenwand mit Dämmung",
          externalRefs: { heinzeOz: "016.06.0010" }, // heinze-pin: Aussenwand Holztafelbau, WDVS, d=295mm
          langtextTemplate:
            "Holzrahmenbauwand mit Putzfassade: Holzständer KVH 60×200 + Mineralwolle-Dämmung 200 mm, OSB beidseitig, Holzwerkstoffplatte außen. Norm DIN EN 1995.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Gipsplatte raumseitig 12,5 mm + Q2",
          langtextTemplate:
            "Innenseitige Gipskartonplatte 12,5 mm auf Holzrahmen-Außenwand, Q2-Spachtelung. Norm DIN 18183-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG02",
          untergruppe: "50",
          ozPattern: "02.50.{N}",
          stlbReferenz: "STLB-Bau 047 WDVS",
          bezeichnung: "Putzträgerdämmung + Außenputz",
          externalRefs: { heinzeOz: "023.03.0100" }, // heinze-pin: Wandputz, armiert, auf HWL-Daemmplatten
          langtextTemplate:
            "Putzträgerdämmung 80–120 mm + mineralischer Außenputz 10 mm auf Holzrahmen-Außenwand, einschl. Armierungsmörtel + Gewebe. Norm DIN 55699.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "AW09",
    fullCode: "AW-CLT-VHF",
    gewerk: "AW",
    category: "outer",
    shortLabel: "Brettsperrholz + VHF",
    description:
      "Brettsperrholz/CLT-Außenwand mit Außendämmung und hinterlüfteter Fassade — Innenoberfläche, CLT, Außendämmung, UK, Hinterlüftung, Bekleidung",
    bauweise: "CLT (Brettsperrholz)",
    tragend: "ja",
    schichten: [
      "CLT-Sichtoberfläche 100–140",
      "Außendämmung 140–200",
      "UK + Hinterlüftung 40–60",
      "Bekleidung 12–25",
    ],
    totalThicknessMmRange: [292, 425],
    defaultLayers: [
      {
        gewerk: "rohbau",
        preset: "custom",
        materialName: "CLT-Brettsperrholz",
        dickeMm: 120,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Außendämmung",
        dickeMm: 160,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Unterkonstruktion + Hinterlüftung",
        dickeMm: 50,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Fassadenbekleidung",
        dickeMm: 18,
      },
    ],
    normReferenz: ["DIN EN 1995", "DIN 4108", "DIN 18516-1"],
    einsatz: "moderner Holz-Massivbau",
    defaultFacadeCode: "FAS04",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG12",
          untergruppe: "20",
          ozPattern: "12.20.{N}",
          stlbReferenz: "STLB-Bau 035 Holzbauarbeiten",
          bezeichnung: "Brettsperrholz CLT-Außenwand 100–140 mm",
          langtextTemplate:
            "Außenwand in Brettsperrholz (CLT) 100–140 mm, raumseitig sichtbar oder beschichtet, einschl. Verarbeitung der Wandanschlüsse. Norm DIN EN 1995.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG09",
          untergruppe: "20",
          ozPattern: "09.20.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Vorgehängte hinterlüftete Fassade auf CLT",
          langtextTemplate:
            "Außendämmung Mineralwolle/Holzfaser 140–200 mm, Aluminium- oder Holz-UK, Hinterlüftung 40–60 mm, Bekleidung 12–25 mm (Holz/Faserzement/HPL). Norm DIN 18516-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },

  // ============================================================================
  // INNENWÄNDE
  // ============================================================================
  {
    code: "IW01",
    fullCode: "IW-MW-115-PUTZ",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Mauerwerk 115 nicht-tragend",
    description: "Nichttragende Mauerwerksinnenwand 115mm, beidseitig verputzt",
    bauweise: "Mauerwerk",
    tragend: "nein",
    schichten: ["Putz 15", "Mauerwerk 115", "Putz 15"],
    totalThicknessMmRange: [145, 175],
    defaultLayers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "KS-Trennwand",
        dickeMm: 115,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 2,
      },
    ],
    normReferenz: ["DIN EN 1996"],
    einsatz: "robuste Raumtrennung",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Innenwand KS 115 mm nicht-tragend",
          externalRefs: { heinzeOz: "012.02.0360" }, // heinze-pin: Mauerwerk KS XL-PE 16-1,8 d=11,5cm
          langtextTemplate:
            "Nichttragende Innenwand in Kalksandstein-Trennwand, Stärke 115 mm, einschl. Mörtel. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm beidseitig",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Mauerwerk-Innenwand, beidseitig, Stärke 15 mm pro Seite, einschl. Eckschienen. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "(wand.flaecheM2 - oeffnungenM2) * 2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW02",
    fullCode: "IW-MW-175-TRAG",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Mauerwerk 175 tragend",
    description: "Tragende Mauerwerksinnenwand 175mm, beidseitig verputzt",
    bauweise: "Mauerwerk",
    tragend: "ja",
    schichten: ["Putz 15", "Mauerwerk 175", "Putz 15"],
    totalThicknessMmRange: [205, 240],
    defaultLayers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Tragmauerwerk (KS / Porenbeton)",
        dickeMm: 175,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 2,
      },
    ],
    normReferenz: ["DIN EN 1996"],
    einsatz: "tragende Innenwand",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "10",
          ozPattern: "02.10.{N}",
          stlbReferenz: "STLB-Bau 011 Mauerarbeiten",
          bezeichnung: "Mauerwerk Innenwand KS/Porenbeton 175 mm tragend",
          externalRefs: { heinzeOz: "012.02.0020" }, // heinze-pin: Mauerwerk KSV 12-1,8 d=17,5cm
          langtextTemplate:
            "Tragende Innenwand in Kalksandstein oder Porenbeton, Stärke 175 mm, einschl. Mörtel. Norm DIN EN 1996.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Innenputz Q2 15 mm beidseitig",
          externalRefs: { heinzeOz: "023.01.0010" }, // heinze-pin: Wandputz PII Q2
          langtextTemplate:
            "Innenputz Q2 auf Mauerwerk-Innenwand, beidseitig, Stärke 15 mm pro Seite. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "(wand.flaecheM2 - oeffnungenM2) * 2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW03",
    fullCode: "IW-BET-200",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Stahlbeton 200",
    description:
      "Stahlbetoninnenwand 200mm, tragend oder aussteifend — beidseitig Spachtel/Putz",
    bauweise: "Stahlbeton",
    tragend: "aussteifend",
    schichten: ["Spachtel 5", "Stahlbeton 200", "Spachtel 5"],
    totalThicknessMmRange: [180, 300],
    defaultLayers: [
      {
        gewerk: "rohbau",
        preset: "beton",
        materialName: "Stahlbeton",
        dickeMm: 200,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Spachtel",
        dickeMm: 5,
        putzSides: 2,
      },
    ],
    normReferenz: ["DIN EN 1992", "DIN 4109"],
    einsatz: "tragend, aussteifend, Brand-/Schallschutz",
    benoetigteFachplanung: ["architekt", "tragwerk", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "20",
          ozPattern: "02.20.{N}",
          stlbReferenz: "STLB-Bau 013 Betonarbeiten",
          bezeichnung: "Stahlbeton-Innenwand 200 mm",
          langtextTemplate:
            "Stahlbeton-Innenwand C25/30, Stärke 200 mm, einschl. Schalung beidseitig, Bewehrung und Verdichten. Norm DIN EN 1992, DIN 4109.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "10",
          ozPattern: "04.10.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Spachtel Q2 5 mm beidseitig auf Beton",
          externalRefs: { heinzeOz: "034.01.0300" }, // heinze-pin: Wandspachtelung, vollflaechig, PIV, Q2
          langtextTemplate:
            "Spachtel Q2 auf Stahlbeton-Wandfläche, Stärke 5 mm, beidseitig, einschl. Eckschienen und Vorbereitung. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "(wand.flaecheM2 - oeffnungenM2) * 2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW04",
    fullCode: "IW-TB-CW75-1L",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Trockenbau CW75 einfach",
    description:
      "Trockenbauwand CW75, einfach beplankt — 12,5 Gipsplatte / CW75 + Dämmung / 12,5 Gipsplatte",
    bauweise: "Trockenbau",
    tragend: "nein",
    schichten: ["Gipsplatte 12,5", "CW75 + Dämmung 75", "Gipsplatte 12,5"],
    totalThicknessMmRange: [100, 100],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Gipsplatte beidseitig",
        dickeMm: 25,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "CW75 + Mineralwolle",
        dickeMm: 75,
      },
    ],
    normReferenz: ["DIN 18183-1"],
    einsatz: "Standard-Innenwand",
    benoetigteFachplanung: ["architekt", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Trockenbauwand CW75 einfach beplankt mit Dämmung",
          externalRefs: { heinzeOz: "039.01.0010" }, // heinze-pin: W111 GKB 100mm 47dB
          langtextTemplate:
            "Trockenbauwand-System einfach beplankt: CW-Profil 75 mm mit Mineralwolle-Dämmung, beidseitig je 1× Gipskartonplatte 12,5 mm, Q2-Spachtelung, einschl. UW-Anschlüsse. Norm DIN 18183-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW05",
    fullCode: "IW-TB-CW75-2L",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Trockenbau CW75 doppelt",
    description:
      "Trockenbauwand CW75, doppelt beplankt — 2× 12,5 Gipsplatte / CW75 + Dämmung / 2× 12,5 Gipsplatte",
    bauweise: "Trockenbau",
    tragend: "nein",
    schichten: ["2× Gipsplatte 25", "CW75 + Dämmung 75", "2× Gipsplatte 25"],
    totalThicknessMmRange: [125, 125],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "2× Gipsplatte beidseitig",
        dickeMm: 50,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "CW75 + Mineralwolle",
        dickeMm: 75,
      },
    ],
    normReferenz: ["DIN 18183-1", "DIN 4109"],
    einsatz: "bessere Stabilität / Schall / Brand",
    benoetigteFachplanung: ["architekt", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Trockenbauwand CW75 doppelt beplankt",
          externalRefs: { heinzeOz: "039.01.0030" }, // heinze-pin: W112 GKB 125mm 55dB EI30
          langtextTemplate:
            "Trockenbauwand-System doppelt beplankt: CW-Profil 75 mm mit Mineralwolle-Dämmung, beidseitig je 2× Gipskartonplatte 12,5 mm, Q2-Spachtelung, einschl. UW-Anschlüsse, Schall- und Brandschutz erhöht. Norm DIN 18183-1, DIN 4109.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW06",
    fullCode: "IW-TB-DOPPELSTAENDER",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Doppelständerwand Schallschutz",
    description:
      "Doppelständerwand für erhöhten Schallschutz — getrennte CW-Profile, Hohlraumdämmung, beidseitig mehrlagige Beplankung",
    bauweise: "Trockenbau",
    tragend: "nein",
    schichten: [
      "2× Gipsplatte 25",
      "CW + Dämmung 50",
      "Trennfuge 5",
      "CW + Dämmung 50",
      "2× Gipsplatte 25",
    ],
    totalThicknessMmRange: [155, 250],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Doppelständer-Trockenbau (komplett)",
        dickeMm: 200,
      },
    ],
    normReferenz: ["DIN 18183-1", "DIN 4109"],
    einsatz: "Wohnungstrennwand, hoher Schallschutz",
    benoetigteFachplanung: ["architekt", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Doppelständerwand Schallschutz Wohnungstrennwand",
          externalRefs: { heinzeOz: "039.01.0200" }, // heinze-pin: W115 GKF 155mm 67dB EI90 (Doppelstaender)
          langtextTemplate:
            "Doppelständer-Trockenbauwand für Wohnungstrennwand mit hohem Schallschutz: zwei getrennte CW-Profile à 50 mm mit je Mineralwolle-Dämmung, Trennfuge 5 mm, beidseitig je 2× Gipskartonplatte 12,5 mm, Q2-Spachtelung. Norm DIN 18183-1, DIN 4109.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW07",
    fullCode: "IW-VS-MW",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Vorsatzschale vor Massivwand",
    description:
      "Vorsatzschale vor Massivwand — Massivwand + Abstand/Profil + Dämmung + Gipsplatte ein- oder zweilagig",
    bauweise: "Trockenbau (Vorsatzschale)",
    tragend: "n/a",
    schichten: ["Massivwand", "Profil + Dämmung 50–100", "Gipsplatte 12,5–25"],
    totalThicknessMmRange: [62, 125],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "CW-Profil + Dämmung",
        dickeMm: 75,
      },
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Gipsplatte",
        dickeMm: 12.5,
      },
    ],
    normReferenz: ["DIN 18183-1", "DIN 4109"],
    einsatz: "Schall, Installation, Sanierung",
    benoetigteFachplanung: ["architekt", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Vorsatzschale vor Massivwand",
          externalRefs: { heinzeOz: "039.01.1200" }, // heinze-pin: W623 GKB 52,5mm direkt (Vorsatzschale)
          langtextTemplate:
            "Vorsatzschale auf Massivwand: CW-Profil 50–75 mm mit Mineralwolle-Dämmung, einlagige Beplankung 1× Gipskartonplatte 12,5 mm, Q2-Spachtelung, einschl. Anschlüsse. Genutzt für Schall, Installation oder Sanierung. Norm DIN 18183-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "IW08",
    fullCode: "IW-NASSRAUM-TB",
    gewerk: "IW",
    category: "inner",
    shortLabel: "Feuchtraum-Trockenbau",
    description:
      "Feuchtraum-Trockenbauwand mit imprägnierten Platten und Abdichtung — Profile + Dämmung + Spezial-Gipsplatten",
    bauweise: "Trockenbau (Nassraum)",
    tragend: "nein",
    schichten: [
      "Imprägn. Gipsplatte 12,5",
      "CW + Dämmung 50–75",
      "Imprägn. Gipsplatte 12,5",
      "Abdichtung",
    ],
    totalThicknessMmRange: [100, 150],
    defaultLayers: [
      {
        gewerk: "trockenbau",
        preset: "trockenbau",
        materialName: "Imprägnierte Gipsplatte beidseitig",
        dickeMm: 25,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "CW + Dämmung",
        dickeMm: 75,
      },
      {
        gewerk: "sonstiges",
        preset: "custom",
        materialName: "Abdichtung Verbundabdichtung",
        dickeMm: 2,
      },
    ],
    normReferenz: ["DIN 18183-1", "DIN 18534"],
    einsatz: "Bad, WC, Hauswirtschaft",
    benoetigteFachplanung: ["architekt", "bauphysik"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "30",
          ozPattern: "04.30.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "Feuchtraum-Trockenbauwand imprägnierte GK-Platten",
          langtextTemplate:
            "Feuchtraum-Trockenbauwand-System CW 75 mm mit Mineralwolle-Dämmung, beidseitig 1× imprägnierte Gipskartonplatte 12,5 mm (GKBI), Q2-Spachtelung, einschl. UW-Anschlüsse und Eckschutz. Norm DIN 18183-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
        {
          hauptgruppe: "HG10",
          untergruppe: "10",
          ozPattern: "10.10.{N}",
          stlbReferenz: "STLB-Bau 052 Abdichtung",
          bezeichnung: "Verbundabdichtung Nassbereich AIV-F",
          externalRefs: { heinzeOz: "014.00.0170" }, // heinze-pin: Boden-/Wandabdichtung A/W2-I innen, Dichtschlaemme
          langtextTemplate:
            "Verbundabdichtung im Sockelbereich von Trockenbauwänden in Nassbereichen, Beanspruchungsklasse W3-I (mäßig), einschl. Eck-/Boden-Wand-Anschlussverstärkung und Dichtmanschetten an Durchführungen. Norm DIN 18534-3.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 * 0.5",
        },
      ],
    },
  },

  // ============================================================================
  // FASSADEN
  // ============================================================================
  {
    code: "FAS01",
    fullCode: "FAS-PUTZ",
    gewerk: "FAS",
    shortLabel: "Putzfassade",
    description: "Außenputz auf Mauerwerk oder WDVS",
    geeignetFuer: ["AW-MW-MONO-PUTZ", "AW-MW-WDVS-PUTZ", "AW-HR-PUTZ"],
    normReferenz: ["DIN EN 13914-1"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "20",
          ozPattern: "04.20.{N}",
          stlbReferenz: "STLB-Bau 024 Außenputz",
          bezeichnung: "Außenputz mineralisch / Silikatharz Schlussbeschichtung",
          externalRefs: { heinzeOz: "023.02.0040" }, // heinze-pin: Aussenwandputz PII + Kunstharz, 2lg
          langtextTemplate:
            "Außenputz Schlussbeschichtung mineralisch oder Silikatharz auf Armierungsputz, Korn 1.5–3 mm, einschl. Eckprofile, Sockelschiene und Anstrich nach Wahl. Norm DIN EN 13914-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS02",
    fullCode: "FAS-KLINKER",
    gewerk: "FAS",
    shortLabel: "Klinker / Verblender",
    description:
      "Verblendmauerwerk mit Luftschicht oder Kerndämmung — separate Außenschale",
    geeignetFuer: ["AW-MW-2S-KLINKER"],
    normReferenz: ["DIN EN 1996", "DIN 1053-4"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    /**
     * Sprint P (2026-05-03) — Mengen-Quelle ist NICHT polygon-basiert (Ansicht-
     * Pläne haben keine Räume), sondern die Σ aus den `ansichtData.verblender-
     * FlaecheM2`-Werten aller `ansicht-fassade`-Runs einer Mappe. Das Σ wird
     * von Sprint X (Cross-Plan-Aggregation) geliefert. Pfad-Konvention:
     *   `mappe.ansichtData.verblenderFlaecheM2` — Σ über N/O/S/W
     */
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG02",
          untergruppe: "30",
          ozPattern: "02.30.{N}",
          stlbReferenz: "STLB-Bau 023 Verblendmauerwerk",
          bezeichnung:
            "Verblendmauerwerk Klinker NF, Luftschicht 4 cm + Kerndämmung",
          externalRefs: { heinzeOz: "012.09.0070" }, // heinze-pin: Vormauerschale VMz, NF, MW 160mm
          langtextTemplate:
            "Verblendmauerwerk aus Klinker im Format NF (240×115×71 mm), Mörtelgruppe NM IIa, mit 4 cm Luftschicht und Kerndämmung WLG 035 zwischen Tragschale und Verblender. Anker und Stürze nach DIN 1053-4. Mauerwerksfugen sauber ausgekratzt; Verfugung mit zementgebundenem Fugenmörtel.",
          mengenEinheit: "m²",
          mengenFormel: "mappe.ansichtData.verblenderFlaecheM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS03",
    fullCode: "FAS-VHF-PLATTE",
    gewerk: "FAS",
    shortLabel: "VHF Plattenfassade",
    description:
      "Vorgehängte hinterlüftete Plattenfassade (Faserzement / HPL / Metall / Keramik) — Unterkonstruktion, Hinterlüftung, Plattenbekleidung",
    geeignetFuer: ["AW-MW-VHF-PLATTE", "AW-BET-VHF", "AW-HR-VHF-PLATTE", "AW-CLT-VHF"],
    normReferenz: ["DIN 18516-1"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "20",
          ozPattern: "09.20.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "VHF Plattenbekleidung Faserzement / HPL",
          externalRefs: { heinzeOz: "038.01.0010" }, // heinze-pin: VHF Faserzementplatte, sichtbare Befestigung
          langtextTemplate:
            "Vorgehängte hinterlüftete Fassade aus Faserzement-, HPL-, Metall- oder Keramikplatten 8–20 mm, sichtbar oder verdeckt befestigt auf Aluminium- oder Holz-Unterkonstruktion mit Hinterlüftung 40 mm. Norm DIN 18516-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS04",
    fullCode: "FAS-VHF-HOLZ-VERT",
    gewerk: "FAS",
    shortLabel: "Holzschalung vertikal",
    description:
      "Vertikale Holzschalung — Boden-Deckel, Leisten, Deckelschalung",
    geeignetFuer: ["AW-MW-VHF-HOLZ", "AW-HR-VHF-HOLZ", "AW-CLT-VHF"],
    normReferenz: ["DIN 18516-1", "DIN 68800"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Holzschalung vertikal (Lärche/Douglasie)",
          langtextTemplate:
            "Vertikale Holzschalung aus Lärche/Douglasie 22–28 mm, Boden-Deckel- oder Leistenausführung, auf Lattung 30–40 mm mit Hinterlüftung. Vorbehandlung Imprägnierung gem. DIN 68800-3, ggf. Anstrich offenporig. Norm DIN 18516-1.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS05",
    fullCode: "FAS-VHF-HOLZ-HORIZ",
    gewerk: "FAS",
    shortLabel: "Holzschalung horizontal",
    description:
      "Horizontale Holzschalung — Rhombus, Stülpschalung, Boden-Deckel horizontal",
    geeignetFuer: ["AW-MW-VHF-HOLZ", "AW-HR-VHF-HOLZ", "AW-CLT-VHF"],
    normReferenz: ["DIN 18516-1", "DIN 68800"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Holzschalung horizontal",
          langtextTemplate:
            "Horizontale Holzschalung aus Lärche/Douglasie 22–28 mm (Rhombus, Stülp oder Boden-Deckel horizontal), auf Lattung 30–40 mm mit Hinterlüftung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS06",
    fullCode: "FAS-DOPPELSCHALUNG",
    gewerk: "FAS",
    shortLabel: "Doppelverschalung Boden-Deckel",
    description:
      "Boden-Deckel- oder Doppelverschalung außen — vertikale Bretter mit Deckleisten oder überdeckenden Brettern",
    geeignetFuer: ["AW-MW-VHF-HOLZ", "AW-HR-VHF-HOLZ"],
    normReferenz: ["DIN 18516-1", "DIN 68800"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Boden-Deckel-Schalung Holz",
          externalRefs: { heinzeOz: "016.03.0020" }, // heinze-pin: Boden-Deckel-Schalung, Laerche rauh, WD180mm
          langtextTemplate:
            "Boden-Deckel-Schalung aus Lärche/Douglasie: Bodenbretter 22 mm + Deckleisten 22 mm, auf Lattung 30–40 mm mit Hinterlüftung, einschl. Anstrich/Imprägnierung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS07",
    fullCode: "FAS-STUELP",
    gewerk: "FAS",
    shortLabel: "Stülpschalung",
    description: "Horizontal überlappende Bretter (Holzfassade)",
    geeignetFuer: ["AW-MW-VHF-HOLZ", "AW-HR-VHF-HOLZ"],
    normReferenz: ["DIN 18516-1", "DIN 68800"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Stülpschalung Lärche/Douglasie",
          externalRefs: { heinzeOz: "016.03.0060" }, // heinze-pin: Stuelpschalung, Laerche gehobelt, WD180mm
          langtextTemplate:
            "Stülpschalung aus Lärche/Douglasie 22–28 mm, horizontal mit Überlappung, auf Lattung 30–40 mm mit Hinterlüftung, einschl. Anstrich/Imprägnierung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },
  {
    code: "FAS08",
    fullCode: "FAS-RHOMBUS",
    gewerk: "FAS",
    shortLabel: "Rhombusschalung",
    description:
      "Offene oder teiloffene Holzleistenfassade, meist hinterlüftet",
    geeignetFuer: ["AW-MW-VHF-HOLZ", "AW-HR-VHF-HOLZ", "AW-CLT-VHF"],
    normReferenz: ["DIN 18516-1", "DIN 68800"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG09",
          untergruppe: "30",
          ozPattern: "09.30.{N}",
          stlbReferenz: "STLB-Bau 052 Fassadenarbeiten",
          bezeichnung: "Rhombusleisten-Schalung Holz",
          externalRefs: { heinzeOz: "016.03.0070" }, // heinze-pin: Rhombus-Leisten, Laerche, WD180mm
          langtextTemplate:
            "Rhombusleisten-Schalung aus Lärche/Douglasie ca. 27×68 mm, Achsabstand 70–90 mm, auf Lattung 30–40 mm mit hinterlegter UV-stabiler Folie und Hinterlüftung, einschl. Anstrich/Imprägnierung. Norm DIN 18516-1, DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "wand.flaecheM2 - oeffnungenM2",
          abzugFormel: "vob_oeffnungen_ueber_2_5m2",
        },
      ],
    },
  },

  // ============================================================================
  // BÖDEN (Sprint B-Reduced 2026-05-02)
  // ============================================================================
  {
    code: "BO-PARKETT-KLEB",
    fullCode: "BO-PARKETT-KLEB",
    gewerk: "BO",
    shortLabel: "Parkett vollflächig verklebt",
    description:
      "Parkett-Belag (Eiche/Esche/Buche) auf Estrich vollflächig verklebt, einschl. Sockelleisten",
    normReferenz: ["DIN 18356", "DIN EN 13489"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden", raumTypen: ["wohnen", "schlafen", "büro", "flur"] },
    defaultLayers: [
      { gewerk: "rohbau", preset: "custom", materialName: "Zement-Estrich CT (Sub-Aufbau)", dickeMm: 50 },
      { gewerk: "sonstiges", preset: "custom", materialName: "Parkettkleber", dickeMm: 1 },
      { gewerk: "oberflaeche", preset: "custom", materialName: "Parkett Eiche 14 mm", dickeMm: 14 },
    ],
    totalThicknessMmRange: [60, 70],
    bauweise: "Bodenaufbau (Estrich + Parkett verklebt)",
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "06",
          ozPattern: "04.06.{N}",
          stlbReferenz: "STLB-Bau 036 Parkett- und Holzpflasterarbeiten",
          bezeichnung: "Parkett 14 mm Eiche, vollflächig auf Estrich verklebt",
          externalRefs: { heinzeOz: "028.02.0120" }, // heinze-pin: Fertigparkett Eiche 2schichtig verklebt
          langtextTemplate:
            "Parkett-Belag aus Eiche, Stärke 14 mm, vollflächig mit Parkettkleber auf vorbereitetem Estrich verkleben, einschl. Schleifen und 2× Versiegeln. Norm DIN 18356.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
        {
          hauptgruppe: "HG04",
          untergruppe: "06",
          ozPattern: "04.06.{N}",
          stlbReferenz: "STLB-Bau 036",
          bezeichnung: "Sockelleiste Parkett 16/40 mm umlaufend",
          externalRefs: { heinzeOz: "028.04.0010" }, // heinze-pin: Sockelleiste Eiche 10x60mm
          langtextTemplate:
            "Sockelleiste aus Massivholz, 16×40 mm, passend zum Parkett-Belag, an die Wand geklebt oder geklipst. Norm DIN 18356.",
          mengenEinheit: "m",
          mengenFormel: "polygon.umfangM",
        },
      ],
    },
  },
  {
    code: "BO-FLIESEN-30x60",
    fullCode: "BO-FLIESEN-30x60",
    gewerk: "BO",
    shortLabel: "Bodenfliesen 30×60 Feinsteinzeug",
    description:
      "Feinsteinzeug-Fliesen 30×60 cm, im Dünnbettverfahren auf Estrich, einschl. Sockelfliese und Verfugung",
    normReferenz: ["DIN 18352", "DIN EN 14411"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden", raumTypen: ["bad", "wc", "küche", "diele"] },
    defaultLayers: [
      { gewerk: "rohbau", preset: "custom", materialName: "Zement-Estrich CT (Sub-Aufbau)", dickeMm: 60 },
      { gewerk: "sonstiges", preset: "custom", materialName: "Dünnbettmörtel", dickeMm: 4 },
      { gewerk: "oberflaeche", preset: "custom", materialName: "Feinsteinzeug 30×60 cm", dickeMm: 10 },
    ],
    totalThicknessMmRange: [70, 80],
    bauweise: "Bodenaufbau (Estrich + Fliesen im Dünnbett)",
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "05",
          ozPattern: "05.05.{N}",
          stlbReferenz: "STLB-Bau 025 Fliesen- und Plattenarbeiten",
          bezeichnung: "Bodenfliesen Feinsteinzeug 30×60 cm im Dünnbettverfahren",
          externalRefs: { heinzeOz: "024.01.0090" }, // heinze-pin: Bodenfliesen 30x60cm kalibriert
          langtextTemplate:
            "Bodenfliesen aus Feinsteinzeug, Format 30×60 cm, Rutschhemmklasse R10, im Dünnbettverfahren auf Estrich verlegen, einschl. Fugenmörtel zementär und Silikon-Anschlussfugen. Norm DIN 18352.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
        {
          hauptgruppe: "HG05",
          untergruppe: "05",
          ozPattern: "05.05.{N}",
          stlbReferenz: "STLB-Bau 025",
          bezeichnung: "Sockelfliese 8 cm hoch umlaufend",
          externalRefs: { heinzeOz: "024.01.0100" }, // heinze-pin: Sockelleiste Fliesen H=10cm
          langtextTemplate:
            "Sockelfliese aus Feinsteinzeug, Höhe 8 cm, passend zum Bodenbelag, im Dünnbettverfahren an die Wand kleben. Norm DIN 18352.",
          mengenEinheit: "m",
          mengenFormel: "polygon.umfangM",
        },
      ],
    },
  },
  {
    code: "BO-VINYL-KLEB",
    fullCode: "BO-VINYL-KLEB",
    gewerk: "BO",
    shortLabel: "Vinyl-Belag vollflächig verklebt",
    description:
      "Designboden Vinyl/LVT, vollflächig auf Estrich verklebt, einschl. Sockelleisten",
    normReferenz: ["DIN 18365", "DIN EN 651"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden", raumTypen: ["büro", "flur", "küche"] },
    defaultLayers: [
      { gewerk: "rohbau", preset: "custom", materialName: "Zement-Estrich CT (Sub-Aufbau)", dickeMm: 60 },
      { gewerk: "sonstiges", preset: "custom", materialName: "Dispersionskleber", dickeMm: 1 },
      { gewerk: "oberflaeche", preset: "custom", materialName: "Designboden Vinyl/LVT 5 mm", dickeMm: 5 },
    ],
    totalThicknessMmRange: [60, 70],
    bauweise: "Bodenaufbau (Estrich + Vinyl verklebt)",
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "07",
          ozPattern: "04.07.{N}",
          stlbReferenz: "STLB-Bau 035 Bodenbelagarbeiten",
          bezeichnung: "Designboden Vinyl/LVT 5 mm vollflächig verklebt",
          externalRefs: { heinzeOz: "036.03.0100" }, // heinze-pin: Vinyl (LVT) Platte 2,5mm Kl.34 R9
          langtextTemplate:
            "Designboden Vinyl/LVT, Stärke 5 mm, vollflächig mit Dispersionskleber auf vorbereitetem Estrich verkleben. Norm DIN 18365.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "BO-TEPPICH",
    fullCode: "BO-TEPPICH",
    gewerk: "BO",
    shortLabel: "Teppichboden Auslage",
    description:
      "Textiler Bodenbelag (Teppich) auf Estrich verlegt, lose oder geklebt",
    normReferenz: ["DIN 18365", "DIN EN 1307"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden", raumTypen: ["büro", "schlafen"] },
    defaultLayers: [
      { gewerk: "rohbau", preset: "custom", materialName: "Zement-Estrich CT (Sub-Aufbau)", dickeMm: 60 },
      { gewerk: "sonstiges", preset: "custom", materialName: "Dispersionskleber", dickeMm: 1 },
      { gewerk: "oberflaeche", preset: "custom", materialName: "Teppichboden Velours", dickeMm: 8 },
    ],
    totalThicknessMmRange: [65, 75],
    bauweise: "Bodenaufbau (Estrich + Teppich verklebt)",
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "07",
          ozPattern: "04.07.{N}",
          stlbReferenz: "STLB-Bau 035",
          bezeichnung: "Teppichboden Velours objekttauglich vollflächig verklebt",
          externalRefs: { heinzeOz: "036.07.0010" }, // heinze-pin: Velours getuftet Bahn 5,5mm Kl.33
          langtextTemplate:
            "Teppichboden velours objekttauglich, Klassifikation 32, vollflächig mit Dispersionskleber auf Estrich verkleben. Norm DIN 18365.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "BO-EST-CT35",
    fullCode: "BO-EST-CT35",
    gewerk: "BO",
    shortLabel: "Zement-Estrich CT35 (Sub-Aufbau)",
    description:
      "Zement-Estrich CT-C25-F4, schwimmend auf Trittschalldämmung, als Sub-Aufbau für Bodenbeläge",
    normReferenz: ["DIN 18560-1", "DIN 18560-2"],
    benoetigteFachplanung: ["architekt", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "boden", isDefault: true },
    defaultLayers: [
      { gewerk: "sonstiges", preset: "custom", materialName: "Trittschalldämmung", dickeMm: 30 },
      { gewerk: "rohbau", preset: "custom", materialName: "Zement-Estrich CT-C25-F4 schwimmend", dickeMm: 60 },
    ],
    totalThicknessMmRange: [85, 95],
    bauweise: "Schwimmender Estrich (Sub-Aufbau)",
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "04",
          ozPattern: "04.04.{N}",
          stlbReferenz: "STLB-Bau 022 Estricharbeiten",
          bezeichnung: "Zement-Estrich CT-C25-F4 schwimmend, Stärke 60 mm",
          externalRefs: { heinzeOz: "024.00.0120" }, // heinze-pin: Estrich auf Trennschicht 60mm F5
          langtextTemplate:
            "Zement-Estrich CT-C25-F4, schwimmend auf Trittschalldämmung 30 mm, Stärke 60 mm, oberflächenfertig für Bodenbeläge. Norm DIN 18560-2.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },

  // ============================================================================
  // WANDOBERFLÄCHEN (Sprint B-Reduced 2026-05-02)
  // ============================================================================
  {
    code: "WA-MAL-DISP",
    fullCode: "WA-MAL-DISP",
    gewerk: "WA",
    shortLabel: "Anstrich Dispersion 2× innen",
    description:
      "Wandanstrich Dispersion 2× weiß auf Putz/Tapete; m² zählt 2× für die Anstriche",
    normReferenz: ["DIN 18363", "DIN EN 13300"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "wand-innen", isDefault: true },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020 Maler- und Lackiererarbeiten",
          bezeichnung: "Wandanstrich Dispersion weiß 2× rollen",
          externalRefs: { heinzeOz: "034.02.0020" }, // heinze-pin: Dispersionsbeschichtung Wand NAK2
          langtextTemplate:
            "Wandanstrich Dispersion weiß 2× im Roll- oder Streichverfahren auf vorbereitetem Untergrund (Putz Q2 oder Tapete). Norm DIN 18363, DIN EN 13300.",
          mengenEinheit: "m²",
          mengenFormel: "(polygon.wandflaecheInnenM2 - oeffnungenM2) * 2",
        },
      ],
    },
  },
  {
    code: "WA-TAPEZ-VLIES",
    fullCode: "WA-TAPEZ-VLIES",
    gewerk: "WA",
    shortLabel: "Vlies-Tapete + 2× Anstrich",
    description:
      "Vlies-Tapete Renoviervlies + 2× Dispersionsanstrich; zwei separate LV-Positionen",
    normReferenz: ["DIN 18363", "DIN EN 13300"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "wand-innen" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020",
          bezeichnung: "Vlies-Tapete Renoviervlies kleistern + tapezieren",
          externalRefs: { heinzeOz: "037.00.0280" }, // heinze-pin: Glas-/Malervlies, vollflaechig (Renoviervlies)
          langtextTemplate:
            "Vlies-Tapete (Renoviervlies, glatt, ca. 150 g/m²) auf vorbereitetem Putz Q2 mit Vlieskleister tapezieren, Stoßfugen Naht-an-Naht. Norm DIN 18363.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.wandflaecheInnenM2 - oeffnungenM2",
        },
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020",
          bezeichnung: "Anstrich auf Tapete Dispersion 2× weiß",
          externalRefs: { heinzeOz: "034.02.0020" }, // heinze-pin: Dispersionsbeschichtung Wand NAK2
          langtextTemplate:
            "Wandanstrich Dispersion weiß 2× auf vorbereiteter Vlies-Tapete. Norm DIN EN 13300.",
          mengenEinheit: "m²",
          mengenFormel: "(polygon.wandflaecheInnenM2 - oeffnungenM2) * 2",
        },
      ],
    },
  },
  {
    code: "WA-TAPEZ-RAUH",
    fullCode: "WA-TAPEZ-RAUH",
    gewerk: "WA",
    shortLabel: "Raufaser + 2× Anstrich",
    description:
      "Raufaser-Tapete + 2× Dispersionsanstrich; zwei separate LV-Positionen",
    normReferenz: ["DIN 18363"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "wand-innen" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020",
          bezeichnung: "Raufaser-Tapete grob kleistern + tapezieren",
          externalRefs: { heinzeOz: "037.01.0060" }, // heinze-pin: Raufasertapete Wand, grobe Koernung
          langtextTemplate:
            "Raufaser-Tapete grob auf vorbereitetem Putz Q2 mit Tapetenkleister tapezieren, Stoßfugen Naht-an-Naht. Norm DIN 18363.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.wandflaecheInnenM2 - oeffnungenM2",
        },
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020",
          bezeichnung: "Anstrich auf Raufaser Dispersion 2× weiß",
          externalRefs: { heinzeOz: "034.02.0020" }, // heinze-pin: Dispersionsbeschichtung Wand NAK2
          langtextTemplate:
            "Wandanstrich Dispersion weiß 2× auf vorbereiteter Raufaser-Tapete. Norm DIN EN 13300.",
          mengenEinheit: "m²",
          mengenFormel: "(polygon.wandflaecheInnenM2 - oeffnungenM2) * 2",
        },
      ],
    },
  },
  {
    code: "WA-FLIESE-30x30",
    fullCode: "WA-FLIESE-30x30",
    gewerk: "WA",
    shortLabel: "Wandfliesen 30×30 bis 2.0 m",
    description:
      "Wandfliesen Steingut/Feinsteinzeug 30×30 cm in Bad/Küche bis Höhe 2.0 m",
    normReferenz: ["DIN 18352", "DIN EN 14411", "DIN 18534"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer"],
    vorschlagFuer: { bauteilTyp: "wand-innen", raumTypen: ["bad", "wc", "küche"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "05",
          ozPattern: "05.05.{N}",
          stlbReferenz: "STLB-Bau 025",
          bezeichnung: "Wandfliesen 30×30 Steingut, Höhe bis 2.0 m, Bad",
          externalRefs: { heinzeOz: "024.03.0040" }, // heinze-pin: Wandfliesen 30x30cm nicht kalibriert
          langtextTemplate:
            "Wandfliesen aus Steingut oder Feinsteinzeug, Format 30×30 cm, im Dünnbettverfahren auf Verbundabdichtung kleben, bis Höhe 2.0 m über FFB, einschl. Fugenmörtel und Silikon-Anschlussfugen. Norm DIN 18352, DIN 18534.",
          mengenEinheit: "m²",
          mengenFormel: "min(polygon.wandflaecheInnenM2, polygon.umfangM * 2.0) - oeffnungenM2",
        },
      ],
    },
  },
  {
    code: "WA-LASUR",
    fullCode: "WA-LASUR",
    gewerk: "WA",
    shortLabel: "Naturholz-Lasur",
    description:
      "Lasur transparent auf Holzschalung/Sichtholz innen, 2-3× streichen",
    normReferenz: ["DIN 18363", "DIN 68800"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "wand-innen" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "04",
          ozPattern: "05.04.{N}",
          stlbReferenz: "STLB-Bau 020",
          bezeichnung: "Naturholz-Lasur transparent 2× streichen",
          langtextTemplate:
            "Lasur transparent auf Sichtholz/Holzschalung innen, 2× streichen, Holzart und Farbton nach Wahl. Norm DIN 18363.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.wandflaecheInnenM2 - oeffnungenM2",
        },
      ],
    },
  },

  // ============================================================================
  // DECKEN (Sprint B-Reduced 2026-05-02)
  // ============================================================================
  {
    code: "DE-GK-ABH-Q2",
    fullCode: "DE-GK-ABH-Q2",
    gewerk: "DE",
    shortLabel: "GK-Decke abgehängt Q2",
    description:
      "Gipskarton-Decke abgehängt, Unterkonstruktion CD60/CD27, einlagig 12,5 mm, Q2-Spachtelung",
    normReferenz: ["DIN 18181", "DIN 18168-1"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "decke", isDefault: true },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "02",
          ozPattern: "04.02.{N}",
          stlbReferenz: "STLB-Bau 028 Trockenbauarbeiten",
          bezeichnung: "GK-Decke abgehängt Q2, einlagig 12,5 mm",
          externalRefs: { heinzeOz: "039.02.0010" }, // heinze-pin: D111 Abhangdecke 1x GKB
          langtextTemplate:
            "Decke abgehängt aus Gipskartonplatten 12,5 mm einlagig auf Unterkonstruktion CD60/CD27 mit Direktabhängern, Spachtelung Q2, einschl. Schattenfugen-Profil an den Wänden. Norm DIN 18181.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.deckenflaecheM2",
        },
      ],
    },
  },
  {
    code: "DE-PUTZ-Q3",
    fullCode: "DE-PUTZ-Q3",
    gewerk: "DE",
    shortLabel: "Decken-Putz Q3 auf Beton",
    description:
      "Innenputz Q3 auf Stahlbeton-Decke, Gipsputz oder mineralisch geglättet",
    normReferenz: ["DIN EN 13914-2"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "decke" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "03",
          ozPattern: "05.03.{N}",
          stlbReferenz: "STLB-Bau 023 Putz- und Stuckarbeiten",
          bezeichnung: "Decken-Innenputz Q3 auf Beton, 8 mm",
          externalRefs: { heinzeOz: "034.01.0690" }, // heinze-pin: Deckenputz PIV Q3
          langtextTemplate:
            "Innenputz Q3 auf Stahlbeton-Decke, Gipsputz oder mineralisch, Stärke 8 mm, geglättet und filzfertig für Anstrich. Norm DIN EN 13914-2.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.deckenflaecheM2",
        },
      ],
    },
  },
  {
    code: "DE-AKUSTIK",
    fullCode: "DE-AKUSTIK",
    gewerk: "DE",
    shortLabel: "Akustik-Decken-Element",
    description:
      "Akustikdecke aus Mineralfaser- oder Holzwolle-Platten in T-Schienen-System abgehängt",
    normReferenz: ["DIN 18041", "DIN EN 13964"],
    benoetigteFachplanung: ["architekt", "akustikplaner"],
    vorschlagFuer: { bauteilTyp: "decke", raumTypen: ["büro", "besprechung", "schule"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG04",
          untergruppe: "02",
          ozPattern: "04.02.{N}",
          stlbReferenz: "STLB-Bau 028",
          bezeichnung: "Akustikdecke Mineralfaser 600×600 in T-Schiene",
          langtextTemplate:
            "Akustikdecke abgehängt aus Mineralfaser-Platten 600×600 mm in T-Schienen-System, Schallabsorption Klasse A, einschl. Wand-L-Profilen. Norm DIN EN 13964, DIN 18041.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.deckenflaecheM2",
        },
      ],
    },
  },

  // ============================================================================
  // SANITÄR-OBJEKTE (Sprint B-Reduced 2026-05-02 — Stückzahl-basiert)
  // ============================================================================
  {
    code: "SAN-WC",
    fullCode: "SAN-WC",
    gewerk: "SAN",
    shortLabel: "Wand-WC mit UP-Spülkasten",
    description:
      "Wand-WC tiefspülend mit Unterputz-Spülkasten und Betätigungsplatte",
    normReferenz: ["DIN EN 997", "DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["bad", "wc"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041 Sanitärinstallation",
          bezeichnung: "Wand-WC tiefspülend mit UP-Spülkasten und Drückerplatte",
          langtextTemplate:
            "Wand-WC tiefspülend, Keramik weiß, einschl. Vorwand-Element mit UP-Spülkasten 6/3 l, Betätigungsplatte chrom, WC-Sitz absenkautomatik, anschließen an Trinkwasser DN15 und Abwasser DN90. Norm DIN EN 997.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.WC",
        },
      ],
    },
  },
  {
    code: "SAN-WT",
    fullCode: "SAN-WT",
    gewerk: "SAN",
    shortLabel: "Waschtisch 60 cm",
    description:
      "Waschtisch 60 cm Keramik mit Einhand-Mischbatterie und Geruchsverschluss",
    normReferenz: ["DIN EN 14688", "DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["bad", "wc"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041",
          bezeichnung: "Waschtisch 60 cm Keramik mit Einhand-Mischer",
          langtextTemplate:
            "Waschtisch Keramik 60 cm, Hahnloch mittig, einschl. Einhand-Mischbatterie verchromt, Geruchsverschluss und Eckventile, anschließen an Trinkwasser DN15 und Abwasser DN50. Norm DIN EN 14688.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.WT",
        },
      ],
    },
  },
  {
    code: "SAN-DUSCHE",
    fullCode: "SAN-DUSCHE",
    gewerk: "SAN",
    shortLabel: "Dusche bodengleich 90×90",
    description:
      "Dusche bodengleich 90×90 cm mit Linienentwässerung und Thermostat-Mischer",
    normReferenz: ["DIN 18534", "DIN EN 1253", "DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer", "abdichtungsplaner"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["bad"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041",
          bezeichnung: "Dusche bodengleich 90×90 mit Linienentwässerung und Thermostat",
          langtextTemplate:
            "Dusche bodengleich 90×90 cm mit Linienentwässerung Edelstahl, Verbundabdichtung nach DIN 18534, Thermostat-Mischer chrom, Brausestange und Handbrause, anschließen an Trinkwasser DN15 und Abwasser DN50. Norm DIN 18534.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.DUSCHE",
        },
      ],
    },
  },
  {
    code: "SAN-BADEWANNE",
    fullCode: "SAN-BADEWANNE",
    gewerk: "SAN",
    shortLabel: "Badewanne 170×75",
    description:
      "Stahl-Email-Badewanne 170×75 cm mit Wannenträger und Thermostat-Mischer",
    normReferenz: ["DIN EN 232", "DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["bad"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041",
          bezeichnung: "Badewanne Stahl-Email 170×75 mit Wannenträger und Thermostat",
          langtextTemplate:
            "Badewanne Stahl-Email 170×75 cm weiß, einschl. Wannenträger Hartschaum, Ab- und Überlauf-Garnitur, Thermostat-Wannenfüll-Mischer chrom, anschließen an Trinkwasser DN15 und Abwasser DN50. Norm DIN EN 232.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.BADEWANNE",
        },
      ],
    },
  },
  {
    code: "SAN-URINAL",
    fullCode: "SAN-URINAL",
    gewerk: "SAN",
    shortLabel: "Wand-Urinal mit IR-Spüler",
    description:
      "Wand-Urinal Keramik mit IR-gesteuertem Spüler",
    normReferenz: ["DIN EN 80", "DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer", "elektroplaner"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["wc", "büro"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041",
          bezeichnung: "Wand-Urinal Keramik mit IR-Spüler",
          langtextTemplate:
            "Wand-Urinal Keramik weiß, einschl. IR-Spüler 230V/Batterie, Vorwand-Element, anschließen an Trinkwasser DN15 und Abwasser DN50. Norm DIN EN 80.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.URINAL",
        },
      ],
    },
  },
  {
    code: "SAN-KUECHENZEILE",
    fullCode: "SAN-KUECHENZEILE",
    gewerk: "SAN",
    shortLabel: "Spülen-Anschluss Küchenzeile",
    description:
      "Sanitär-Anschluss für Küchenzeile (Spüle) mit Mischer und Geräte-Anschluss Geschirrspüler",
    normReferenz: ["DIN 1986-100"],
    benoetigteFachplanung: ["architekt", "tga-sanitaer"],
    vorschlagFuer: { bauteilTyp: "sanitaer-objekt", raumTypen: ["küche"] },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG06",
          untergruppe: "01",
          ozPattern: "06.01.{N}",
          stlbReferenz: "STLB-Bau 041",
          bezeichnung: "Spüle-Anschluss Küchenzeile mit Mischer und Geräte-Eckventil",
          langtextTemplate:
            "Sanitär-Anschluss für Küchenspüle: Einhand-Mischer hoher Auslauf, Geräte-Eckventil für Geschirrspüler, Kalt- und Warmwasser-Eckventile, Geruchsverschluss DN50 mit 2 Anschlüssen. Norm DIN 1986-100.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.sanCount.KUECHENZEILE",
        },
      ],
    },
  },

  // ============================================================================
  // TÜREN (Sprint B-Reduced-TU 2026-05-02 — pro Polygon-Stückzahl)
  // ============================================================================
  // mengenFormel "polygon.tuerCount" zählt pro Polygon ALLE openings[type=door],
  // unabhängig vom Tür-Typ. Für detaillierte Tür-Listen mit pro-Tür-Codes
  // kommt die feinkörnige Verknüpfung in Sprint F.
  {
    code: "TU-INNEN-RS",
    fullCode: "TU-INNEN-RS",
    gewerk: "TU",
    shortLabel: "Innentür Stahlzarge",
    description:
      "Innentür mit Stahlzarge und Holzblatt CPL weiß, einflüglig",
    normReferenz: ["DIN 18101", "DIN 18111"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "tuer", isDefault: true },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "01",
          ozPattern: "05.01.{N}",
          stlbReferenz: "STLB-Bau 016 Tischler-Türen",
          bezeichnung: "Innentür Stahlzarge mit Holzblatt CPL weiß",
          langtextTemplate:
            "Innentür einflüglig, Stahlzarge in Mauerwerk eingeputzt, Türblatt CPL weiß glatt, Bandseite frei wählbar, einschl. Drückergarnitur und Schließblech. Norm DIN 18101.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.tuerCount",
        },
      ],
    },
  },
  {
    code: "TU-INNEN-OS",
    fullCode: "TU-INNEN-OS",
    gewerk: "TU",
    shortLabel: "Innentür mit Glasausschnitt",
    description:
      "Innentür mit Stahlzarge und Holzblatt mit Lichtausschnitt (Klarglas/Mattglas)",
    normReferenz: ["DIN 18101", "DIN 18111"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "tuer" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "01",
          ozPattern: "05.01.{N}",
          stlbReferenz: "STLB-Bau 016",
          bezeichnung: "Innentür Stahlzarge mit Lichtausschnitt",
          langtextTemplate:
            "Innentür einflüglig, Stahlzarge, Türblatt CPL mit Lichtausschnitt (Mattglas oder Klarglas, ESG 6 mm), einschl. Drückergarnitur. Norm DIN 18101.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.tuerCount",
        },
      ],
    },
  },
  {
    code: "TU-WOHN-RC2",
    fullCode: "TU-WOHN-RC2",
    gewerk: "TU",
    shortLabel: "Wohnungseingangstür RC2",
    description:
      "Wohnungseingangstür mit Stahl-Sicherheitszarge, RC2-zertifiziert, schallschutzdämmend",
    normReferenz: ["DIN EN 1627", "DIN 4109"],
    benoetigteFachplanung: ["architekt", "schallschutz"],
    vorschlagFuer: { bauteilTyp: "tuer" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "01",
          ozPattern: "05.01.{N}",
          stlbReferenz: "STLB-Bau 016",
          bezeichnung: "Wohnungseingangstür RC2 mit Schallschutz",
          langtextTemplate:
            "Wohnungseingangstür einflüglig, Stahl-Sicherheitszarge, Türblatt mit RC2-Beschlag (DIN EN 1627), Schalldämmung Rw ≥ 32 dB, Profilzylinder mit Sicherungskarte, einschl. Türspion und Türdrücker. Norm DIN EN 1627, DIN 4109.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.tuerCount",
        },
      ],
    },
  },
  {
    code: "TU-INNEN-SCHIEBE",
    fullCode: "TU-INNEN-SCHIEBE",
    gewerk: "TU",
    shortLabel: "Schiebetür innen",
    description:
      "Innen-Schiebetür mit Laufschiene oben, vor der Wand laufend, Glas oder Holzblatt",
    normReferenz: ["DIN 18101"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "tuer" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "01",
          ozPattern: "05.01.{N}",
          stlbReferenz: "STLB-Bau 016",
          bezeichnung: "Schiebetür innen vor der Wand mit Laufschiene oben",
          langtextTemplate:
            "Schiebetür einflüglig, vor der Wand laufend, Laufschiene oben mit Soft-Stop, Türblatt Holzblatt CPL oder ESG-Glas, einschl. Muscheln und Mitnehmer. Norm DIN 18101.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.tuerCount",
        },
      ],
    },
  },
  {
    code: "TU-AUSSEN-AL",
    fullCode: "TU-AUSSEN-AL",
    gewerk: "TU",
    shortLabel: "Aluminium-Außentür",
    description:
      "Hauseingangstür Aluminium-Profilsystem mit Wärmedämmung und 3-fach Verglasung",
    normReferenz: ["DIN 18055", "DIN EN 14351-1", "GEG"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "tuer" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "01",
          ozPattern: "05.01.{N}",
          stlbReferenz: "STLB-Bau 053 Außentüren",
          bezeichnung: "Aluminium-Hauseingangstür wärmegedämmt mit 3-fach-Verglasung",
          langtextTemplate:
            "Hauseingangstür einflüglig, Aluminium-Profilsystem wärmegedämmt (Uw ≤ 1,2 W/m²K), 3-fach-Verglasung Sicherheitsglas, einschl. Mehrfachverriegelung, Bodendichtung und Schwelle. Norm DIN EN 14351-1, GEG.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.tuerCount",
        },
      ],
    },
  },

  // ============================================================================
  // FENSTER (Sprint B-Reduced-FE 2026-05-02 — pro Polygon-Stückzahl)
  // ============================================================================
  // mengenFormel "polygon.fensterCount" zählt openings[type=window]. Pro Code
  // eine Stückzahl pro Polygon — feinkörnige Mehr-Typen-Differenzierung kommt
  // in Sprint F.
  {
    code: "FE-KST-3F",
    fullCode: "FE-KST-3F",
    gewerk: "FE",
    shortLabel: "Kunststoff-Fenster 3-fach",
    description:
      "Kunststoff-Fenster Standard mit 3-fach-Wärmeschutzverglasung, Dreh-/Kipp-Beschlag",
    normReferenz: ["DIN EN 14351-1", "GEG"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "fenster", isDefault: true },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "02",
          ozPattern: "05.02.{N}",
          stlbReferenz: "STLB-Bau 052 Fenster",
          bezeichnung: "Kunststoff-Fenster Dreh-/Kipp 3-fach-Verglasung",
          langtextTemplate:
            "Kunststoff-Fenster einflüglig, Profil-System mit 6-Kammer-Profil, Dreh-/Kipp-Beschlag, 3-fach-Wärmeschutzverglasung Ug ≤ 0,6 W/m²K, Uw ≤ 1,1 W/m²K, einschl. Anschlussfolie innen/außen, Fensterbank innen MDF beschichtet und außen Aluminium. Norm DIN EN 14351-1, GEG.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.fensterCount",
        },
      ],
    },
  },
  {
    code: "FE-KST-2F",
    fullCode: "FE-KST-2F",
    gewerk: "FE",
    shortLabel: "Kunststoff-Fenster 2-fach",
    description:
      "Kunststoff-Fenster Standard mit 2-fach-Verglasung (z.B. Nebenraum, Garage)",
    normReferenz: ["DIN EN 14351-1"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "fenster" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "02",
          ozPattern: "05.02.{N}",
          stlbReferenz: "STLB-Bau 052",
          bezeichnung: "Kunststoff-Fenster 2-fach-Verglasung",
          langtextTemplate:
            "Kunststoff-Fenster einflüglig, 2-fach-Verglasung Ug ≤ 1,1 W/m²K, Uw ≤ 1,3 W/m²K, einschl. Anschlussfolie und Fensterbank. Norm DIN EN 14351-1.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.fensterCount",
        },
      ],
    },
  },
  {
    code: "FE-HOLZ-3F",
    fullCode: "FE-HOLZ-3F",
    gewerk: "FE",
    shortLabel: "Holz-Fenster 3-fach",
    description:
      "Holz-Fenster Lärche/Fichte mit 3-fach-Wärmeschutzverglasung",
    normReferenz: ["DIN EN 14351-1", "DIN 68800", "GEG"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "fenster" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "02",
          ozPattern: "05.02.{N}",
          stlbReferenz: "STLB-Bau 052",
          bezeichnung: "Holz-Fenster Lärche 3-fach-Wärmeschutzverglasung",
          externalRefs: { heinzeOz: "026.02.0020" }, // heinze-pin: Holzfenster 1flg U=0,93 (3-fach)
          langtextTemplate:
            "Holz-Fenster einflüglig aus Lärchen-Brettschichtholz, Dreh-/Kipp-Beschlag, 3-fach-Wärmeschutzverglasung Uw ≤ 1,0 W/m²K, einschl. Anschlussfolie und 2× Anstrich Lasur außen. Norm DIN EN 14351-1, DIN 68800.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.fensterCount",
        },
      ],
    },
  },
  {
    code: "FE-DACH-3F",
    fullCode: "FE-DACH-3F",
    gewerk: "FE",
    shortLabel: "Dachflächenfenster 3-fach",
    description:
      "Dachflächenfenster Holz/PU innen, 3-fach-Verglasung, mit Eindeckrahmen",
    normReferenz: ["DIN EN 14351-1", "GEG"],
    benoetigteFachplanung: ["architekt", "dachplaner"],
    vorschlagFuer: { bauteilTyp: "fenster" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "02",
          ozPattern: "05.02.{N}",
          stlbReferenz: "STLB-Bau 052",
          bezeichnung: "Dachflächenfenster 3-fach inkl. Eindeckrahmen",
          langtextTemplate:
            "Dachflächenfenster Holz/PU innen, Schwingfenster, 3-fach-Wärmeschutzverglasung Uw ≤ 1,0 W/m²K, einschl. Eindeckrahmen für Pfannendach, Dampfbremsen-Anschluss und Innenfutter. Norm DIN EN 14351-1, GEG.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.fensterCount",
        },
      ],
    },
  },
  {
    code: "FE-FESTVERGLASUNG",
    fullCode: "FE-FESTVERGLASUNG",
    gewerk: "FE",
    shortLabel: "Festverglasung",
    description:
      "Feste Verglasung ohne Öffnungsflügel (z.B. Treppenhaus / Großformat)",
    normReferenz: ["DIN EN 14351-1"],
    benoetigteFachplanung: ["architekt", "fassadenplaner", "bauphysik", "energieberater"],
    vorschlagFuer: { bauteilTyp: "fenster" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG05",
          untergruppe: "02",
          ozPattern: "05.02.{N}",
          stlbReferenz: "STLB-Bau 052",
          bezeichnung: "Festverglasung 3-fach ohne Öffnungsflügel",
          langtextTemplate:
            "Festverglasung im Profil-System Kunststoff oder Aluminium, 3-fach-Wärmeschutzverglasung Uw ≤ 1,1 W/m²K, einschl. Anschlussfolie. Norm DIN EN 14351-1.",
          mengenEinheit: "Stück",
          mengenFormel: "polygon.fensterCount",
        },
      ],
    },
  },

  // ============================================================================
  // GALABAU / AUSSENANLAGEN (Sprint G 2026-05-03)
  //
  // Codes für Polygone OUTSIDE des HAUS-Polygons — Terrassen, Dachterrassen,
  // Balkone, Garten-Wege, Stellplätze. User wählt im Inspector → Auswahl-Tab
  // den Boden-Code; Mengen-Formel rechnet polygon.bodenflaecheM2 + Verschnitt.
  // Mapping in HG07 (Außenanlagen, Kostengruppe 500 nach DIN 276).
  // ============================================================================
  {
    // Sprint #6 GALA-Polish (2026-05-05) — Mutterboden als Vorbereitung
    // für Rasen, Pflanzungen, Hecke. Häufige LV-Position vor Vegetationsbau.
    code: "GA-MUTTERBODEN",
    fullCode: "GA-MUTTERBODEN",
    gewerk: "GA",
    shortLabel: "Mutterboden andecken",
    description:
      "Mutterboden andecken/aufbringen, Stärke 15-30 cm, einschl. Profilieren und Andrücken — Vorbereitung für Rasen-/Pflanz-Flächen",
    normReferenz: ["DIN 18915", "DIN 18300"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "20",
          ozPattern: "07.20.{N}",
          stlbReferenz: "STLB-Bau 320 Vegetationstechnik",
          bezeichnung: "Mutterboden andecken, Stärke 20 cm",
          externalRefs: { heinzeOz: "003.02.0200" }, // heinze-pin: Oberboden andecken d=30cm
          langtextTemplate:
            "Mutterboden gewachsen, Stärke 20 cm im verdichteten Zustand, auf vorbereitetem Untergrund andecken, einschl. Profilieren und leichtem Andrücken. Bodenklasse 1 nach DIN 18300. Norm DIN 18915.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "GA-PFLASTER",
    fullCode: "GA-PFLASTER",
    gewerk: "GA",
    shortLabel: "Pflasterbelag (Beton/Naturstein)",
    description:
      "Pflasterbelag aus Betonstein- oder Naturstein-Pflaster auf Splittbettung, einschl. Randeinfassung",
    normReferenz: ["DIN 18318", "DIN EN 1338", "DIN EN 1342"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "10",
          ozPattern: "07.10.{N}",
          stlbReferenz: "STLB-Bau 318 Pflasterdecken",
          bezeichnung: "Pflasterbelag Betonstein 80 mm auf Splittbettung",
          externalRefs: { heinzeOz: "014.24.0020" }, // heinze-pin: Betonverbundpflaster 10x20x8cm
          langtextTemplate:
            "Pflasterbelag aus Betonpflasterstein, Stärke 80 mm, auf Splittbettung 4 cm und Tragschicht, einschl. Verfugung mit Splitt 0/2 mm. Norm DIN 18318.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
        {
          hauptgruppe: "HG07",
          untergruppe: "10",
          ozPattern: "07.10.{N}",
          stlbReferenz: "STLB-Bau 318",
          bezeichnung: "Randeinfassung Beton-Tiefbord 8/20 cm",
          langtextTemplate:
            "Randeinfassung aus Beton-Tiefbord 8/20 cm in Beton C12/15 verlegen. Norm DIN 18318.",
          mengenEinheit: "m",
          mengenFormel: "polygon.umfangM",
        },
      ],
    },
  },
  {
    code: "GA-HOLZDIELE",
    fullCode: "GA-HOLZDIELE",
    gewerk: "GA",
    shortLabel: "Holzdiele Lärche/Bangkirai (Terrassendeck)",
    description:
      "Terrassendiele aus Lärche oder Bangkirai auf Aluminium-/Holz-Unterkonstruktion",
    normReferenz: ["DIN 68800", "DIN EN 350"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "11",
          ozPattern: "07.11.{N}",
          stlbReferenz: "STLB-Bau 318 Holzbelag im Außenbereich",
          bezeichnung: "Terrassendiele Lärche 27/145 mm auf UK",
          langtextTemplate:
            "Terrassendiele aus heimischer Lärche, Format 27×145 mm, auf Aluminium-Unterkonstruktion auf Stelzlagern montieren, einschl. Edelstahl-Verschraubung. Norm DIN 68800.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "GA-WPC",
    fullCode: "GA-WPC",
    gewerk: "GA",
    shortLabel: "WPC-Diele (Wood Plastic Composite)",
    description:
      "WPC-Terrassendiele auf Aluminium-Unterkonstruktion, einschl. Stelzlager",
    normReferenz: ["DIN EN 15534-4"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "11",
          ozPattern: "07.11.{N}",
          stlbReferenz: "STLB-Bau 318",
          bezeichnung: "WPC-Diele 25/140 mm auf Alu-UK",
          langtextTemplate:
            "WPC-Terrassendiele 25×140 mm, vollvermassiv, auf Aluminium-Unterkonstruktion, einschl. Edelstahl-Clips und Stelzlagern. Norm DIN EN 15534-4.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "GA-STEINPLATTE",
    fullCode: "GA-STEINPLATTE",
    gewerk: "GA",
    shortLabel: "Naturstein-/Keramik-Platte (Terrasse)",
    description:
      "Großformatige Naturstein- oder Keramik-Platte 60×60 cm auf Stelzlagern",
    normReferenz: ["DIN 18332", "DIN EN 1469"],
    benoetigteFachplanung: ["architekt", "bauphysik"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "10",
          ozPattern: "07.10.{N}",
          stlbReferenz: "STLB-Bau 318",
          bezeichnung: "Terrassen-Platte 60×60 cm auf Stelzlagern",
          langtextTemplate:
            "Terrassen-Platte aus Feinsteinzeug oder Naturstein, Format 60×60 cm, Stärke 20 mm, auf höhenverstellbaren Stelzlagern verlegen. Norm DIN 18332.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "GA-RASEN",
    fullCode: "GA-RASEN",
    gewerk: "GA",
    shortLabel: "Rasenfläche (Anlage)",
    description:
      "Rasenanlage durch Aussaat oder Rollrasen auf Mutterbodenschicht",
    normReferenz: ["DIN 18917", "DIN 18915"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "20",
          ozPattern: "07.20.{N}",
          stlbReferenz: "STLB-Bau 320 Vegetationstechnik",
          bezeichnung: "Rasenansaat auf Mutterboden",
          langtextTemplate:
            "Rasenansaat (Sport- oder Gebrauchsrasen) auf 15 cm vorbereitetem Mutterboden, einschl. Walzen und Bewässerung in der Anwuchsphase. Norm DIN 18917.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
  {
    code: "GA-KIES",
    fullCode: "GA-KIES",
    gewerk: "GA",
    shortLabel: "Kies-/Schotterfläche",
    description:
      "Kies- oder Schotterfläche auf Tragschicht, einschl. Randeinfassung",
    normReferenz: ["DIN 18318"],
    benoetigteFachplanung: ["architekt"],
    vorschlagFuer: { bauteilTyp: "boden" },
    lvMapping: {
      positions: [
        {
          hauptgruppe: "HG07",
          untergruppe: "10",
          ozPattern: "07.10.{N}",
          stlbReferenz: "STLB-Bau 318",
          bezeichnung: "Kies-Belag 8/16 mm, Stärke 5 cm",
          langtextTemplate:
            "Kies-Belag rundkörnig 8/16 mm, Stärke 5 cm, auf Frostschutzschicht und Geotextil. Norm DIN 18318.",
          mengenEinheit: "m²",
          mengenFormel: "polygon.bodenflaecheM2",
        },
      ],
    },
  },
];

// ============================================================================
// BACKWARDS-COMPAT KONSTANTEN (gefilterte Views)
// ============================================================================

/**
 * Subset der `DEFAULT_CATALOG` mit nur Wand-Einträgen (AW + IW). Erhalten
 * für Backwards-Compat; neue Aufrufe sollten `DEFAULT_CATALOG` mit
 * `getCatalogEntriesByGewerk` nutzen.
 */
export const DEFAULT_WALL_CATALOG: WallType[] = DEFAULT_CATALOG.filter(
  (e): e is WallType => e.gewerk === "AW" || e.gewerk === "IW"
);

/** Subset der `DEFAULT_CATALOG` mit nur Fassaden-Einträgen (FAS). */
export const DEFAULT_FACADE_CATALOG: FacadeType[] = DEFAULT_CATALOG.filter(
  (e): e is FacadeType => e.gewerk === "FAS"
);

// ============================================================================
// LOOKUPS (gewerk-fokussiert + Backwards-Compat-Wrapper)
// ============================================================================

/** Findet einen Eintrag über `code` ODER `fullCode`. */
export function getCatalogEntryByCode(
  code: string,
  catalog: CatalogEntry[] = DEFAULT_CATALOG
): CatalogEntry | undefined {
  return catalog.find((e) => e.code === code || e.fullCode === code);
}

/** Filtert alle Einträge eines bestimmten Gewerks. */
export function getCatalogEntriesByGewerk(
  gewerk: GewerkPrefix,
  catalog: CatalogEntry[] = DEFAULT_CATALOG
): CatalogEntry[] {
  return catalog.filter((e) => e.gewerk === gewerk);
}

/** Backwards-Compat — narrowt auf `WallType`. */
export function getWallTypeByCode(
  code: string,
  catalog: WallType[] = DEFAULT_WALL_CATALOG
): WallType | undefined {
  return catalog.find((w) => w.code === code || w.fullCode === code);
}

/** Backwards-Compat — narrowt auf `FacadeType`. */
export function getFacadeByCode(
  code: string,
  catalog: FacadeType[] = DEFAULT_FACADE_CATALOG
): FacadeType | undefined {
  return catalog.find((f) => f.code === code || f.fullCode === code);
}

/** Backwards-Compat — filtert AW/IW nach `category`. */
export function getWallTypesByCategory(
  category: WallCategory,
  catalog: WallType[] = DEFAULT_WALL_CATALOG
): WallType[] {
  return catalog.filter((w) => w.category === category);
}

// ============================================================================
// MERGE
// ============================================================================

/**
 * Merge `overrides` ins Default-Set. Patches matchen über `code`; neue
 * Codes werden angehängt. Gibt eine neue Liste zurück (kein Mutate).
 *
 * Backwards-Compat: wenn ein neuer Wand-Patch `gewerk` weglässt aber
 * `category` setzt, wird `gewerk` aus `category` abgeleitet
 * (`outer→AW`, `inner→IW`). Bei Fassaden-Patches wird `gewerk: "FAS"`
 * automatisch gesetzt.
 */
export function mergeWallCatalog(
  defaults: WallType[],
  overrides: WallCatalogOverrides | null
): { walls: WallType[]; facades: FacadeType[] } {
  const walls = defaults.map((w) => ({ ...w }));
  const facades = DEFAULT_FACADE_CATALOG.map((f) => ({ ...f }));

  for (const patch of overrides?.walls ?? []) {
    const idx = walls.findIndex((w) => w.code === patch.code);
    if (idx >= 0) {
      walls[idx] = { ...walls[idx], ...patch };
    } else if (
      patch.fullCode &&
      patch.category &&
      patch.shortLabel &&
      patch.description &&
      patch.bauweise &&
      patch.tragend &&
      patch.schichten &&
      patch.totalThicknessMmRange &&
      patch.defaultLayers &&
      patch.normReferenz &&
      patch.einsatz
    ) {
      const gewerk: "AW" | "IW" =
        patch.gewerk ?? (patch.category === "outer" ? "AW" : "IW");
      walls.push({ ...patch, gewerk } as WallType);
    }
  }
  for (const patch of overrides?.facades ?? []) {
    const idx = facades.findIndex((f) => f.code === patch.code);
    if (idx >= 0) {
      facades[idx] = { ...facades[idx], ...patch };
    } else if (
      patch.fullCode &&
      patch.shortLabel &&
      patch.description &&
      patch.geeignetFuer &&
      patch.normReferenz
    ) {
      facades.push({ ...patch, gewerk: "FAS" } as FacadeType);
    }
  }
  return { walls, facades };
}
